import { detachPushBeforeSignOut } from "./lib/notifications";
import { isAvailableHouseTask } from "./lib/houseTaskScope";
import { useSettingsScrollLock } from "./lib/useSettingsScrollLock";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { createUserWithEmailAndPassword, onIdTokenChanged, sendPasswordResetEmail, sendEmailVerification, signInWithEmailAndPassword, signOut, updateProfile, type MultiFactorResolver, type User } from "firebase/auth";
import { collection, doc, getDoc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "./lib/firebase";
import { AccountSecurityPanel, finishTotpSignIn, hasTotpMfa, mfaResolverFromError, signInWithGoogle } from "./lib/authSecurity";
import { currentPushState, disablePushNotifications, enablePushNotifications, ensurePushNotificationsRegistered, subscribeToForegroundPush, type PushState } from "./lib/notifications";
import { CalendarScreen } from "./components/CalendarScreen";
import { HealthScreen } from "./components/HealthScreen";
import { PublicSupportChat } from "./components/PublicSupportChat";
import { ListsScreen } from "./components/ListsScreen";
import { FamilyGamesWidget } from "./components/FamilyGamesWidget";
import { FamilyBeaconAlert } from "./components/FamilyBeacon";
import { recordFamilyVisit } from "./lib/familyGames";
import { HomeHunt } from "./components/HomeHunt";
import { FamilyBookScreen } from "./components/FamilyBookScreen";
import { FinanceScreen } from "./components/FinanceScreen";
import { FinanceSettingsPanel } from "./components/FinanceSettingsPanel";
import { NotificationPreferencesPanel } from "./components/NotificationPreferencesPanel";
import { NotificationBell } from "./components/NotificationBell";
import { FamilyCheckInCard } from "./components/FamilyCheckInCard";
import { AvatarEditor, AvatarPortrait, type AvatarConfig } from "./components/AvatarEditor";
import {
  DEFAULT_HOME_AI_CONTENT,
  getHomeAiContent,
  greetingPeriod,
  localDateKey
} from "./lib/homeAi";
import {
  countdownValue,
  deleteCalendarEvent,
  eventsForCalendarDay,
  saveCalendarEvent,
  setCountdownVisibility,
  sortUpcomingEvents,
  subscribeToCalendarEvents,
  type FamilyCalendarEvent
} from "./lib/calendarEvents";
import {
  completeAppleCalendarConnection,
  disconnectAppleCalendar,
  disconnectGoogleCalendar,
  finalizeGoogleCalendarConnection,
  getGoogleCalendarConnectionAttempt,
  requestLinkedCalendarCopy,
  startAppleCalendarConnection,
  startGoogleCalendarConnection,
  subscribeToCalendarConnections,
  syncAppleCalendar,
  syncGoogleCalendar,
  writableCalendarConnections,
  type AppleCalendarChoice,
  type CalendarConnection,
  type GoogleCalendarChoice
} from "./lib/calendarConnections";
import { BEACH_OPTIONS, getOceanConditions, type OceanConditions } from "./lib/oceanConditions";
import { getConditionInsights, type ConditionInsights } from "./lib/conditionInsights";
import {
  subscribeToFamilyLists,
  subscribeToHouseholdMembers,
  type FamilyListItem,
  type HouseholdMember,
  type ListKind
} from "./lib/familyLists";

type FamilyProfile = {
  firstName: string;
  lastName: string;
  email: string;
  householdId?: string;
  householdName?: string;
};

type DashboardLocation = { name: string; latitude: number; longitude: number; };
type WeatherData = {
  temperature: number; apparentTemperature: number; high: number; low: number;
  precipitationChance: number; precipitationAmount: number; precipitationHours: number;
  windSpeed: number; windGust: number; windDirection: number; relativeHumidity: number;
  uvIndex: number; weatherCode: number; sunrise: string; sunset: string;
  hourly: Array<{
    time: string; temperature: number; uvIndex: number; windSpeed: number;
    windGust: number; precipitationChance: number; precipitationAmount: number;
  }>;
};

type WeatherView = "overview" | "uv" | "wind" | "precipitation";

type FamilyInvite = {
  id: string;
  email: string;
  role: string;
  status: string;
  resendCount?: number;
  createdAt?: { toDate?: () => Date };
  resendRequestedAt?: { toDate?: () => Date };
  householdId?: string;
  householdName?: string;
  deliveryStatus?: string;
  acceptedBy?: string;
};

function familyRoleLabel(role: string) {
  if (role === "owner") return "Household owner";
  if (role === "admin") return "Parent / Admin";
  if (role === "adult") return "Adult member";
  if (role === "teen") return "Teen member";
  if (role === "child") return "Child member";
  return "Family member";
}

function canonicalInviteEmail(value: string) {
  const normalized = value.trim().toLowerCase();
  const at = normalized.lastIndexOf("@");
  if (at < 1) return normalized;
  let local = normalized.slice(0, at);
  let domain = normalized.slice(at + 1);
  if (domain === "googlemail.com") domain = "gmail.com";
  if (domain === "gmail.com") local = local.split("+")[0].replaceAll(".", "");
  return `${local}@${domain}`;
}

const navItems = [
  { label: "Home", icon: "⌂" },
  { label: "Family Book", icon: "♡" },
  { label: "Calendar", icon: "▣" },
  { label: "Lists", icon: "☷" },
  { label: "Health", icon: "♡" },
  { label: "Finances", icon: "$" },
  { label: "Expenses", icon: "≡" },
  { label: "Income", icon: "+" },
  { label: "Investments", icon: "↗" }
];

const DEFAULT_LOCATION: DashboardLocation = { name: "San Clemente, CA", latitude: 33.4269, longitude: -117.6119 };


function weatherLabel(code: number) {
  if (code === 0) return "Clear";
  if (code <= 3) return "Partly cloudy";
  if (code <= 48) return "Foggy";
  if (code <= 67) return "Rain";
  if (code <= 77) return "Snow";
  if (code <= 82) return "Showers";
  return "Thunderstorms";
}

function uvLabel(index: number) {
  if (index < 3) return "Low";
  if (index < 6) return "Moderate";
  if (index < 8) return "High";
  if (index < 11) return "Very high";
  return "Extreme";
}

function compassDirection(degrees: number) {
  const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return directions[Math.round(degrees / 45) % 8];
}

function formatClock(value?: string) {
  if (!value) return "—";
  return new Date(value).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function moonInfo(date: Date) {
  const lunarCycle = 29.53058867;
  const knownNewMoon = Date.UTC(2000, 0, 6, 18, 14);
  const age = ((date.getTime() - knownNewMoon) / 86_400_000 % lunarCycle + lunarCycle) % lunarCycle;
  const illumination = Math.round((1 - Math.cos(2 * Math.PI * age / lunarCycle)) * 50);
  const waxing = age < lunarCycle / 2;
  const phase = age < 1.85 || age > 27.68 ? "New Moon"
    : age < 5.54 ? "Waxing Crescent" : age < 9.23 ? "First Quarter"
    : age < 12.92 ? "Waxing Gibbous" : age < 16.61 ? "Full Moon"
    : age < 20.30 ? "Waning Gibbous" : age < 23.99 ? "Last Quarter" : "Waning Crescent";
  const targets = [
    { name: "First Quarter", age: lunarCycle * .25 },
    { name: "Full Moon", age: lunarCycle * .5 },
    { name: "Last Quarter", age: lunarCycle * .75 },
    { name: "New Moon", age: lunarCycle }
  ];
  const next = targets.find((target) => target.age > age) || targets[0];
  const days = Math.max(1, Math.round(((next.age > age ? next.age : next.age + lunarCycle) - age)));
  return { phase, illumination, waxing, nextPhase: next.name, days };
}

function getGreeting(hour: number) {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

export default function App() {
  const [now, setNow] = useState(() => new Date());
  const [homeAiContent, setHomeAiContent] = useState(DEFAULT_HOME_AI_CONTENT);
  const [activeNav, setActiveNav] = useState("Home");
  const [activeListMode, setActiveListMode] = useState<ListKind>("shopping");
  const [listItems, setListItems] = useState<FamilyListItem[]>([]);
  const [householdMembers, setHouseholdMembers] = useState<HouseholdMember[]>([]);
  const [pingStatus, setPingStatus] = useState("");
  const [pushState, setPushState] = useState<PushState>("off");
  const [pushBusy, setPushBusy] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  useSettingsScrollLock(authOpen);
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState<"location" | "finance" | "calendars" | "notifications" | "family" | "account" | null>(null);
  const [roleInfoOpen, setRoleInfoOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authNotice, setAuthNotice] = useState("");
  const [authMode, setAuthMode] = useState<"signin" | "signup" | "reset">("signin");
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("oauth_state_id")) {
      setSettingsSection("finance");
      setAuthOpen(true);
    }
  }, []);
  const [profile, setProfile] = useState<FamilyProfile | null>(null);
  const [inviteStatus, setInviteStatus] = useState("");
  const [invitations, setInvitations] = useState<FamilyInvite[]>([]);
  const [incomingInvite, setIncomingInvite] = useState<FamilyInvite | null>(null);
  const [householdLocation, setHouseholdLocation] = useState<DashboardLocation>(DEFAULT_LOCATION);
  const [deviceLocation, setDeviceLocation] = useState<DashboardLocation | null>(null);
  const [locationMode, setLocationMode] = useState<"household" | "current">(
    () => window.localStorage.getItem("famdashfun.locationMode") === "current" ? "current" : "household"
  );
  const [locationStatus, setLocationStatus] = useState("");
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [weatherStatus, setWeatherStatus] = useState("Loading local conditions…");
  const [weatherView, setWeatherView] = useState<WeatherView>("overview");
  const [beachSlug, setBeachSlug] = useState(() => window.localStorage.getItem("famdashfun.beachSlug") || "poche-beach");
  const [oceanMode, setOceanMode] = useState<"home" | "current">("home");
  const [ocean, setOcean] = useState<OceanConditions | null>(null);
  const [oceanStatus, setOceanStatus] = useState("Loading NOAA ocean conditions…");
  const [conditionInsights, setConditionInsights] = useState<ConditionInsights | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<FamilyCalendarEvent[]>([]);
  const [countdownOpen, setCountdownOpen] = useState(false);
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState<FamilyCalendarEvent | null>(null);
  const [countdownBusy, setCountdownBusy] = useState(false);
  const [countdownStatus, setCountdownStatus] = useState("");
  const [destinationConnectionIds, setDestinationConnectionIds] = useState<string[]>([]);
  const [eventEditorDate, setEventEditorDate] = useState(() => localDateKey(new Date()));
  const [eventEditorCountdownDefault, setEventEditorCountdownDefault] = useState(true);
  const [eventEditorAllDay, setEventEditorAllDay] = useState(true);
  const [calendarConnectionStatus, setCalendarConnectionStatus] = useState("");
  const [calendarConnections, setCalendarConnections] = useState<CalendarConnection[]>([]);
  const [appleCalendars, setAppleCalendars] = useState<AppleCalendarChoice[]>([]);
  const [appleCalendarKey, setAppleCalendarKey] = useState("");
  const [appleConnectionAttemptId, setAppleConnectionAttemptId] = useState("");
  const [appleConnectOpen, setAppleConnectOpen] = useState(false);
  const [appleCalendarBusy, setAppleCalendarBusy] = useState(false);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendarChoice[]>([]);
  const [googleCalendarId, setGoogleCalendarId] = useState("");
  const [googleConnectionAttemptId, setGoogleConnectionAttemptId] = useState("");
  const [googleCalendarBusy, setGoogleCalendarBusy] = useState(false);
  const activeLocation = locationMode === "current" && deviceLocation ? deviceLocation : householdLocation;
  const homeAiRefreshKey = `${localDateKey(now)}-${greetingPeriod(now.getHours())}`;
  const shoppingHasItems = listItems.some((item) => item.kind === "shopping" && !item.done);
  const houseHasItems = listItems.some((item) => isAvailableHouseTask(item, user?.uid));

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    currentPushState().then(setPushState).catch(() => setPushState("unsupported"));
    let unsubscribe: () => void = () => {};
    subscribeToForegroundPush((payload) => {
      setPingStatus(payload.data?.body || payload.notification?.body || "A new family alert arrived.");
    }).then((stop) => { unsubscribe = stop; }).catch(() => undefined);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || !("Notification" in window)) return;
    let active = true;
    let busy = false;
    let lastAttempt = 0;
    const refresh = () => {
      if (!active || document.visibilityState === "hidden" || busy || Date.now() - lastAttempt < 60000) return;
      if (Notification.permission !== "granted") return;
      lastAttempt = Date.now();
      busy = true;
      ensurePushNotificationsRegistered()
        .then(() => { if (active) setPushState("on"); })
        .catch(() => {
          if (active) {
            setPushState("off");
            setPingStatus("This device could not register for alerts. Open Settings → Notifications to check it.");
          }
        })
        .finally(() => { busy = false; });
    };
    refresh();
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("online", refresh);
    return () => { active = false; document.removeEventListener("visibilitychange", refresh); window.removeEventListener("online", refresh); };
  }, [user]);

  useEffect(() => {
    if (!user || !profile?.householdId) {
      setHomeAiContent(DEFAULT_HOME_AI_CONTENT);
      return;
    }

    let active = true;
    getHomeAiContent(
      profile.householdId,
      new Date(),
      shoppingHasItems,
      houseHasItems
    )
      .then((content) => {
        if (active) setHomeAiContent(content);
      })
      .catch(() => {
        if (active) setHomeAiContent(DEFAULT_HOME_AI_CONTENT);
      });

    return () => {
      active = false;
    };
  }, [user, profile?.householdId, homeAiRefreshKey, shoppingHasItems, houseHasItems]);

  useEffect(() => {
    if (!profile?.householdId) {
      setCalendarEvents([]);
      return;
    }

    return subscribeToCalendarEvents(
      profile.householdId,
      setCalendarEvents,
      () => setCountdownStatus("Calendar events are unavailable right now.")
    );
  }, [profile?.householdId]);

  async function handleEnablePush() {
    setPushBusy(true);
    setPingStatus("");
    try {
      await enablePushNotifications();
      setPushState("on");
      setPingStatus("Push alerts are on for this device.");
    } catch (error) {
      setPushState(await currentPushState());
      setPingStatus(error instanceof Error ? error.message : "Push alerts could not be enabled.");
    } finally { setPushBusy(false); }
  }

  async function handleDisablePush() {
    setPushBusy(true);
    try {
      await disablePushNotifications();
      setPushState("off");
      setPingStatus("This device will no longer receive push alerts.");
    } catch { setPingStatus("This device could not be removed right now."); }
    finally { setPushBusy(false); }
  }

  useEffect(() => {
    if (!profile?.householdId) {
      setListItems([]);
      setHouseholdMembers([]);
      return;
    }
    const stopLists = subscribeToFamilyLists(profile.householdId, setListItems, () => setListItems([]));
    const stopMembers = subscribeToHouseholdMembers(profile.householdId, setHouseholdMembers, () => setHouseholdMembers([]));
    return () => {
      stopLists();
      stopMembers();
    };
  }, [profile?.householdId]);

  useEffect(() => {
    if (!profile?.householdId) {
      setCalendarConnections([]);
      return;
    }

    return subscribeToCalendarConnections(
      profile.householdId,
      setCalendarConnections,
      () => setCalendarConnectionStatus("Calendar connections are unavailable right now.")
    );
  }, [profile?.householdId]);

  useEffect(() => {
    if (!profile?.householdId) return;
    const params = new URLSearchParams(window.location.search);
    const attemptId = params.get("googleCalendarAttempt");
    const googleError = params.get("googleCalendarError");
    if (!attemptId && !googleError) return;

    setSettingsSection("calendars");
    setAuthOpen(true);
    params.delete("googleCalendarAttempt");
    params.delete("googleCalendarError");
    const nextQuery = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (nextQuery ? `?${nextQuery}` : "") + window.location.hash);

    if (googleError) {
      setCalendarConnectionStatus("Google Calendar could not be connected. Please try again.");
      return;
    }

    setGoogleCalendarBusy(true);
    setCalendarConnectionStatus("Google approved access. Loading your calendars…");
    getGoogleCalendarConnectionAttempt(profile.householdId, attemptId!)
      .then((result) => {
        setGoogleConnectionAttemptId(attemptId!);
        setGoogleCalendars(result.calendars);
        const defaultCalendar = result.calendars.find((calendar) => calendar.primary) || result.calendars[0];
        setGoogleCalendarId(defaultCalendar?.id || "");
        setCalendarConnectionStatus(result.calendars.length
          ? "Choose the Google calendar to connect."
          : "No Google calendars were found.");
      })
      .catch(() => setCalendarConnectionStatus("The Google connection session expired. Please connect again."))
      .finally(() => setGoogleCalendarBusy(false));
  }, [profile?.householdId]);

  useEffect(() => {
    const inviteId = new URLSearchParams(window.location.search).get("invite");
    if (!inviteId) return;
    httpsCallable<{inviteId:string},{householdName:string;role:string;maskedEmail:string}>(functions,"previewHouseholdInvite")({inviteId}).then(({data}) => {
      setIncomingInvite({id:inviteId,email:data.maskedEmail,householdName:data.householdName,role:data.role,status:"pending"});
      setAuthMode("signup");
      setAuthOpen(true);
    }).catch(() => {
      setAuthError("This invitation is not available yet. Please ask the family owner to resend it.");
      setAuthOpen(true);
    });
  }, []);

  useEffect(() => onIdTokenChanged(auth, async (currentUser) => {
    setUser(currentUser);
    setProfile(null);
    try {
      if (currentUser) {
        const snapshot = await getDoc(doc(db, "users", currentUser.uid));
        if (auth.currentUser?.uid !== currentUser.uid) return;
        if (snapshot.exists()) {
          setProfile(snapshot.data() as FamilyProfile);
        } else {
          setAuthOpen(true);
        }
      }
    } finally {
      setAuthReady(true);
    }
  }), []);

  useEffect(() => {
    if (!user || !profile?.householdId) return;
    const universalSections = ["Home", "Calendar", "Lists", "Family Book"];
    const availableSections = hasTotpMfa(user)
      ? [...universalSections, "Finances", "Expenses", "Income", "Investments"]
      : universalSections;
    void recordFamilyVisit(profile.householdId, availableSections).catch(() => undefined);
  }, [user, profile?.householdId]);

  useEffect(() => {
    if (!profile?.householdId) {
      setInvitations([]);
      return;
    }
    return onSnapshot(collection(db, "households", profile.householdId, "invites"), (snapshot) => {
      const rows = snapshot.docs.map((invite) => ({ id: invite.id, ...invite.data() } as FamilyInvite));
      rows.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.().getTime() || 0;
        const bTime = b.createdAt?.toDate?.().getTime() || 0;
        return bTime - aTime;
      });
      setInvitations(rows);
    });
  }, [profile?.householdId]);

  useEffect(() => {
    if (!profile?.householdId) { setHouseholdLocation(DEFAULT_LOCATION); return; }
    return onSnapshot(doc(db, "households", profile.householdId, "settings", "location"), (snapshot) => {
      if (!snapshot.exists()) return;
      const value = snapshot.data() as DashboardLocation;
      if (typeof value.name === "string" && typeof value.latitude === "number" && typeof value.longitude === "number") setHouseholdLocation(value);
    });
  }, [profile?.householdId]);

  useEffect(() => {
    const controller = new AbortController();
    setWeatherStatus("Loading local conditions…");
    const params = new URLSearchParams({
      latitude: String(activeLocation.latitude), longitude: String(activeLocation.longitude),
      current: "temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,wind_speed_10m,wind_direction_10m,wind_gusts_10m,precipitation,rain",
      daily: "temperature_2m_max,temperature_2m_min,precipitation_probability_max,precipitation_sum,precipitation_hours,uv_index_max,wind_speed_10m_max,wind_gusts_10m_max,wind_direction_10m_dominant,sunrise,sunset",
      hourly: "temperature_2m,uv_index,wind_speed_10m,wind_gusts_10m,precipitation_probability,precipitation",
      temperature_unit: "fahrenheit", wind_speed_unit: "mph", precipitation_unit: "inch", timezone: "auto", forecast_days: "1"
    });
    fetch(`https://api.open-meteo.com/v1/forecast?${params}`, { signal: controller.signal })
      .then((response) => { if (!response.ok) throw new Error("Weather unavailable"); return response.json(); })
      .then((data) => {
        setWeather({
          temperature: Math.round(data.current.temperature_2m), apparentTemperature: Math.round(data.current.apparent_temperature),
          high: Math.round(data.daily.temperature_2m_max[0]), low: Math.round(data.daily.temperature_2m_min[0]),
          precipitationChance: Math.round(data.daily.precipitation_probability_max[0] || 0),
          precipitationAmount: Number(data.daily.precipitation_sum[0] || 0),
          precipitationHours: Number(data.daily.precipitation_hours[0] || 0),
          windSpeed: Math.round(data.current.wind_speed_10m),
          windGust: Math.round(data.current.wind_gusts_10m || data.daily.wind_gusts_10m_max[0] || 0),
          windDirection: Math.round(data.current.wind_direction_10m || data.daily.wind_direction_10m_dominant[0] || 0),
          relativeHumidity: Math.round(data.current.relative_humidity_2m || 0),
          uvIndex: Number(data.daily.uv_index_max[0] || 0),
          weatherCode: data.current.weather_code,
          sunrise: data.daily.sunrise[0], sunset: data.daily.sunset[0],
          hourly: (data.hourly?.time || []).map((time: string, index: number) => ({
            time,
            temperature: Number(data.hourly.temperature_2m?.[index] || 0),
            uvIndex: Number(data.hourly.uv_index?.[index] || 0),
            windSpeed: Number(data.hourly.wind_speed_10m?.[index] || 0),
            windGust: Number(data.hourly.wind_gusts_10m?.[index] || 0),
            precipitationChance: Number(data.hourly.precipitation_probability?.[index] || 0),
            precipitationAmount: Number(data.hourly.precipitation?.[index] || 0)
          }))
        });
        setWeatherStatus("");
      })
      .catch((error) => { if (error.name !== "AbortError") setWeatherStatus("Local conditions are unavailable right now."); });
    return () => controller.abort();
  }, [activeLocation.latitude, activeLocation.longitude]);

  useEffect(() => {
    let active = true;
    setOceanStatus("Loading NOAA ocean conditions…");
    getOceanConditions(beachSlug, oceanMode === "current" && deviceLocation ? deviceLocation : undefined)
      .then((value) => {
        if (!active) return;
        setOcean(value);
        setOceanStatus("");
      })
      .catch(() => {
        if (!active) return;
        setOcean(null);
        setOceanStatus("Live NOAA ocean conditions are unavailable right now.");
      });
    return () => { active = false; };
  }, [beachSlug, oceanMode, deviceLocation]);

  function chooseOceanHomeBeach() {
    setOceanMode("home");
    setLocationStatus("Showing conditions for the household home beach.");
  }

  function chooseOceanCurrentLocation() {
    if (!navigator.geolocation) {
      setLocationStatus("This device does not support location services.");
      return;
    }
    setLocationStatus("Finding the nearest tide station and buoy…");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setDeviceLocation({ name: "My current location", latitude: position.coords.latitude, longitude: position.coords.longitude });
        setOceanMode("current");
        setLocationStatus("Showing ocean conditions nearest this device.");
      },
      () => setLocationStatus("Location permission was not granted. Poche Beach is still active."),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 }
    );
  }

  function chooseHouseholdLocation() {
    window.localStorage.setItem("famdashfun.locationMode", "household");
    setLocationMode("household");
    setLocationStatus(`Using ${householdLocation.name} on this device.`);
  }

  function chooseCurrentLocation() {
    if (!navigator.geolocation) { setLocationStatus("This device does not support location services."); return; }
    setLocationStatus("Requesting this device’s location…");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setDeviceLocation({ name: "My current location", latitude: position.coords.latitude, longitude: position.coords.longitude });
        setLocationMode("current");
        window.localStorage.setItem("famdashfun.locationMode", "current");
        setLocationStatus("Weather, tides, and sunlight now follow this device.");
      },
      () => setLocationStatus("Location permission was not granted. The household location is still active."),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 }
    );
  }

  function openEventEditor(
    event: FamilyCalendarEvent | null = null,
    dateKey = localDateKey(new Date()),
    countdownDefault = true
  ) {
    if (!user || !profile?.householdId) {
      setAuthError("Sign in to manage family calendar events.");
      setAuthOpen(true);
      return;
    }
    setSelectedCalendarEvent(event);
    setEventEditorDate(event?.dateKey || dateKey);
    setEventEditorCountdownDefault(event?.showOnHome ?? countdownDefault);
    setEventEditorAllDay(!event?.time);
    setDestinationConnectionIds([]);
    setCountdownStatus("");
    setCountdownOpen(true);
  }

  function closeSettings() {
    if (needsProfile || needsFamily || needsInviteAcceptance) return;
    setSettingsSection(null);
    setAuthOpen(false);
  }

  function openCalendarConnections() {
    if (!user || !profile?.householdId) {
      setAuthError("Sign in to manage calendar connections.");
      setAuthOpen(true);
      return;
    }
    setCalendarConnectionStatus("");
    setSettingsSection("calendars");
    setAuthOpen(true);
  }

  function openLists(mode: ListKind) {
    if (!user || !profile?.householdId) {
      setAuthError("Sign in to manage family lists.");
      setAuthOpen(true);
      return;
    }
    setActiveListMode(mode);
    setActiveNav("Lists");
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.getElementById(mode === "shopping" ? "shopping-list-input" : "house-list-input")?.focus({ preventScroll: true });
    });
  }

  async function handleStartGoogleCalendarConnection() {
    if (!profile?.householdId) return;
    setGoogleCalendarBusy(true);
    setCalendarConnectionStatus("Opening secure Google sign-in…");
    try {
      const result = await startGoogleCalendarConnection(profile.householdId, window.location.href);
      window.location.assign(result.authUrl);
    } catch {
      setGoogleCalendarBusy(false);
      setCalendarConnectionStatus("Google Calendar sign-in could not start. Please try again.");
    }
  }

  async function handleConnectGoogleCalendar() {
    if (!profile?.householdId || !googleCalendarId || !googleConnectionAttemptId) return;
    setGoogleCalendarBusy(true);
    setCalendarConnectionStatus("Connecting Google Calendar…");
    try {
      const result = await finalizeGoogleCalendarConnection(
        profile.householdId,
        googleConnectionAttemptId,
        googleCalendarId
      );
      const sync = await syncGoogleCalendar(profile.householdId, result.connectionId);
      setGoogleCalendars([]);
      setGoogleCalendarId("");
      setGoogleConnectionAttemptId("");
      setCalendarConnectionStatus(`${result.displayName} is connected ${result.canCreateEvents ? "for read and write" : "as read-only"}. Imported ${sync.imported} events.`);
    } catch {
      setCalendarConnectionStatus("We couldn’t finish the Google connection. Please connect again.");
    } finally {
      setGoogleCalendarBusy(false);
    }
  }

  async function handleDisconnectGoogleCalendar(connection: CalendarConnection) {
    if (!profile?.householdId || !window.confirm(`Disconnect ${connection.displayName}? Imported events will remain visible until removed.`)) return;
    setGoogleCalendarBusy(true);
    try {
      await disconnectGoogleCalendar(profile.householdId, connection.id);
      setCalendarConnectionStatus(`${connection.displayName} was disconnected and Google access was revoked.`);
    } catch {
      setCalendarConnectionStatus("That Google calendar could not be disconnected.");
    } finally {
      setGoogleCalendarBusy(false);
    }
  }

  async function handleSyncGoogleCalendar(connection: CalendarConnection) {
    if (!profile?.householdId) return;
    setGoogleCalendarBusy(true);
    setCalendarConnectionStatus(`Syncing ${connection.displayName}…`);
    try {
      const result = await syncGoogleCalendar(profile.householdId, connection.id);
      setCalendarConnectionStatus(`${connection.displayName} is up to date. Imported or refreshed ${result.imported} events.`);
    } catch {
      setCalendarConnectionStatus("Google Calendar could not sync right now.");
    } finally {
      setGoogleCalendarBusy(false);
    }
  }

  async function handleStartAppleCalendarConnection(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!profile?.householdId) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setAppleCalendarBusy(true);
    setCalendarConnectionStatus("Securely checking your iCloud calendars…");
    try {
      const result = await startAppleCalendarConnection(
        profile.householdId,
        String(form.get("appleUsername") || "").trim(),
        String(form.get("appleAppSpecificPassword") || "")
      );
      formElement.reset();
      setAppleConnectionAttemptId(result.attemptId);
      setAppleCalendars(result.calendars);
      setAppleCalendarKey(result.calendars[0]?.key || "");
      setCalendarConnectionStatus(result.calendars.length
        ? "Credentials verified. Choose the Apple calendar to connect."
        : "No Apple calendars were found.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      setCalendarConnectionStatus(
        message.includes("temporarily unavailable")
          ? "Apple Calendar is temporarily unavailable. Wait a little while and try again once."
          : message.includes("rejected")
            ? "Apple rejected that Apple Account email or app-specific password."
            : message.includes("secured")
              ? "We couldn’t securely store that calendar connection. Please try again later."
              : "Apple Calendar could not be reached. Please try again later."
      );
    } finally {
      setAppleCalendarBusy(false);
    }
  }

  async function handleConnectAppleCalendar() {
    if (!profile?.householdId || !appleCalendarKey || !appleConnectionAttemptId) return;
    setAppleCalendarBusy(true);
    setCalendarConnectionStatus("Connecting Apple Calendar…");
    try {
      const result = await completeAppleCalendarConnection(
        profile.householdId,
        appleConnectionAttemptId,
        appleCalendarKey
      );
      const connection = await syncAppleCalendar(profile.householdId, result.connectionId);
      setAppleCalendars([]);
      setAppleCalendarKey("");
      setAppleConnectionAttemptId("");
      setAppleConnectOpen(false);
      setCalendarConnectionStatus(`${result.displayName} is connected for read and write. Imported ${connection.imported} upcoming events.`);
    } catch {
      setCalendarConnectionStatus("We couldn’t finish the Apple connection. Please start the connection again.");
    } finally {
      setAppleCalendarBusy(false);
    }
  }

  async function handleDisconnectAppleCalendar(connection: CalendarConnection) {
    if (!profile?.householdId || !window.confirm(`Disconnect ${connection.displayName}? Imported events will remain visible until removed.`)) return;
    setAppleCalendarBusy(true);
    try {
      await disconnectAppleCalendar(profile.householdId, connection.id);
      setCalendarConnectionStatus(`${connection.displayName} was disconnected and its encrypted credentials were deleted.`);
    } catch {
      setCalendarConnectionStatus("That Apple calendar could not be disconnected.");
    } finally {
      setAppleCalendarBusy(false);
    }
  }

  async function handleSyncAppleCalendar(connection: CalendarConnection) {
    if (!profile?.householdId) return;
    setAppleCalendarBusy(true);
    setCalendarConnectionStatus(`Syncing ${connection.displayName}…`);
    try {
      const result = await syncAppleCalendar(profile.householdId, connection.id);
      setCalendarConnectionStatus(`${connection.displayName} is up to date. Imported or refreshed ${result.imported} events.`);
    } catch {
      setCalendarConnectionStatus("Apple Calendar could not sync right now.");
    } finally {
      setAppleCalendarBusy(false);
    }
  }

  async function handleSaveCalendarEvent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user || !profile?.householdId) return;

    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const title = String(form.get("title") || "").trim();
    const dateKey = String(form.get("dateKey") || "");
    if (!title || !dateKey) return;
    const allDay = form.get("allDay") === "on";
    const endDateKey = String(form.get("endDateKey") || "") || null;
    const participantUids = form.getAll("participantUids").map(String);

    setCountdownBusy(true);
    setCountdownStatus("");
    try {
      const canonicalEventId = await saveCalendarEvent(
        profile.householdId,
        user.uid,
        {
          title,
          dateKey,
          endDateKey,
          time: allDay ? null : String(form.get("time") || "") || null,
          endTime: allDay ? null : String(form.get("endTime") || "") || null,
          repeat: String(form.get("repeat") || "none") as "none" | "daily" | "weekly" | "monthly" | "yearly",
          location: String(form.get("location") || ""),
          details: String(form.get("details") || ""),
          participantUids,
          showOnHome: form.get("showOnHome") === "on"
        },
        selectedCalendarEvent
      );

      const destinations = destinationConnectionIds
        .map((connectionId) => calendarConnections.find((connection) => connection.id === connectionId))
        .filter((connection): connection is CalendarConnection => Boolean(connection));
      if (destinations.length) {
        await Promise.all(destinations.map((destination) => requestLinkedCalendarCopy(
          profile.householdId!,
          canonicalEventId,
          destination,
          user.uid,
          {
            destinationTitle: String(form.get("destinationTitle") || title),
            busy: form.get("destinationBusy") === "on",
            includeDetails: form.get("includeDestinationDetails") === "on",
            keepSynchronized: form.get("keepSynchronized") === "on"
          }
        )));
      }

      setCountdownStatus(destinations.length
        ? `${title} saved to the family calendar and queued for ${destinations.map((destination) => destination.displayName).join(" and ")}.`
        : selectedCalendarEvent ? "Calendar event updated." : "Event added to the family calendar.");
      setSelectedCalendarEvent(null);
      setDestinationConnectionIds([]);
      formElement.reset();
    } catch {
      setCountdownStatus("We couldn’t save that event. Please try again.");
    } finally {
      setCountdownBusy(false);
    }
  }

  async function handleDeleteCalendarEvent(event: FamilyCalendarEvent) {
    if (!profile?.householdId || event.source !== "manual") return;
    if (!window.confirm("Delete this event from the family calendar? Copies already sent to Google or Apple will stay there.")) return;
    setCountdownBusy(true);
    setCountdownStatus("");
    try {
      await deleteCalendarEvent(profile.householdId, event.id);
      setCountdownOpen(false);
      setSelectedCalendarEvent(null);
    } catch {
      setCountdownStatus("We couldn’t delete that event. Please try again.");
    } finally {
      setCountdownBusy(false);
    }
  }

  async function handleCountdownVisibility(event: FamilyCalendarEvent, showOnHome: boolean) {
    if (!profile?.householdId) return;
    setCountdownBusy(true);
    setCountdownStatus("");
    try {
      await setCountdownVisibility(profile.householdId, event.id, showOnHome);
      setCountdownStatus(showOnHome ? `${event.title} added to the homepage.` : `${event.title} removed from the homepage.`);
      if (!showOnHome && selectedCalendarEvent?.id === event.id) setSelectedCalendarEvent(null);
    } catch {
      setCountdownStatus("We couldn’t update that countdown. Please try again.");
    } finally {
      setCountdownBusy(false);
    }
  }

  async function handleSaveHouseholdLocation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!profile?.householdId) return;
    const query = String(new FormData(event.currentTarget).get("locationQuery") || "").trim();
    if (!query) return;
    setAuthBusy(true); setLocationStatus("Finding that location…");
    try {
      const response = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=1&language=en&format=json`);
      if (!response.ok) throw new Error("Search unavailable");
      const result = (await response.json()).results?.[0];
      if (!result) { setLocationStatus("We couldn’t find that city or ZIP code."); return; }
      const next = { name: [result.name, result.admin1].filter(Boolean).join(", "), latitude: result.latitude, longitude: result.longitude };
      await setDoc(doc(db, "households", profile.householdId, "settings", "location"), { ...next, updatedAt: serverTimestamp() });
      setHouseholdLocation(next);
      setLocationStatus(`${next.name} is now the household location.`);
    } catch {
      setLocationStatus("We couldn’t update the household location. Owners and admins can change it.");
    } finally { setAuthBusy(false); }
  }

  function completeSignInNavigation() {
    setActiveNav("Home");
    setSettingsSection(null);
    setMoreOpen(false);
    setAuthOpen(false);
  }

  async function handleSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setAuthBusy(true);
    setAuthError("");
    setAuthNotice("");
    try {
      await signInWithEmailAndPassword(
        auth,
        String(form.get("email") || "").trim(),
        String(form.get("password") || "")
      );
      completeSignInNavigation();
    } catch (error) {
      const resolver = mfaResolverFromError(error);
      if (resolver) {
        setMfaResolver(resolver);
        setAuthError("");
      } else {
        setAuthError("That email and password did not match. Please try again.");
      }
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleGoogleSignIn() {
    setAuthBusy(true);
    setAuthError("");
    setAuthNotice("");
    try {
      await signInWithGoogle();
      if (incomingInvite) setAuthOpen(true);
      else completeSignInNavigation();
    } catch (error) {
      const resolver = mfaResolverFromError(error);
      if (resolver) {
        setMfaResolver(resolver);
      } else {
        setAuthError("Google sign-in could not be completed. Make sure you choose the Google account with the same email as your FamDashFun account.");
      }
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleMfaSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!mfaResolver) return;
    setAuthBusy(true);
    setAuthError("");
    try {
      await finishTotpSignIn(mfaResolver, mfaCode);
      setMfaResolver(null);
      setMfaCode("");
      completeSignInNavigation();
    } catch {
      setAuthError("That authenticator code was not accepted. Wait for a new code and try again.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handlePasswordReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const email = String(new FormData(event.currentTarget).get("email") || "").trim().toLowerCase();
    setAuthBusy(true);
    setAuthError("");
    setAuthNotice("");
    try {
      await sendPasswordResetEmail(auth, email);
      setAuthNotice(`Password reset instructions were sent to ${email}. Check spam if they do not arrive in a few minutes.`);
    } catch {
      setAuthError("We could not send the reset email. Check the address and try again.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleCreateAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const firstName = String(form.get("firstName") || "").trim();
    const lastName = String(form.get("lastName") || "").trim();
    const email = String(form.get("email") || "").trim().toLowerCase();
    setAuthBusy(true);
    setAuthError("");
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, String(form.get("password") || ""));
      await updateProfile(credential.user, { displayName: `${firstName} ${lastName}`.trim() });
      const newProfile = { firstName, lastName, email };
      await setDoc(doc(db, "users", credential.user.uid), { ...newProfile, createdAt: serverTimestamp() });
      setProfile(newProfile);
    } catch {
      setAuthError("We could not create that account. The email may already be registered or the password may be too short.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleCompleteProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) return;
    const form = new FormData(event.currentTarget);
    const firstName = String(form.get("firstName") || "").trim();
    const lastName = String(form.get("lastName") || "").trim();
    const completedProfile = { firstName, lastName, email: user.email || "" };
    setAuthBusy(true);
    setAuthError("");
    try {
      await updateProfile(user, { displayName: `${firstName} ${lastName}`.trim() });
      await setDoc(doc(db, "users", user.uid), { ...completedProfile, updatedAt: serverTimestamp() }, { merge: true });
      setProfile(completedProfile);
    } catch {
      setAuthError("We could not save your name. Please try again.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleCreateFamily(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user || !profile) return;
    const form = new FormData(event.currentTarget);
    const householdName = String(form.get("householdName") || "").trim();
    const householdRef = doc(collection(db, "households"));
    setAuthBusy(true);
    setAuthError("");
    try {
      await setDoc(householdRef, { name: householdName, createdBy: user.uid, createdAt: serverTimestamp() });
      await setDoc(doc(db, "households", householdRef.id, "members", user.uid), {
        uid: user.uid, email: user.email, firstName: profile.firstName, lastName: profile.lastName,
        role: "owner", joinedAt: serverTimestamp()
      });
      const nextProfile = { ...profile, householdId: householdRef.id, householdName };
      await setDoc(doc(db, "users", user.uid), { householdId: householdRef.id, householdName }, { merge: true });
      setProfile(nextProfile);
    } catch {
      setAuthError("We could not create the family yet. Please try again.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user || !profile?.householdId) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const email = String(form.get("inviteEmail") || "").trim().toLowerCase();
    const role = String(form.get("role") || "member");
    const existingPending = invitations.find((invite) => invite.status === "pending" && canonicalInviteEmail(invite.email) === canonicalInviteEmail(email));
    if (existingPending) {
      formElement.reset();
      await handleResendInvite(existingPending);
      return;
    }
    setAuthBusy(true);
    setAuthError("");
    setInviteStatus("");
    try {
      await httpsCallable(functions,"createHouseholdInvite")({householdId:profile.householdId,email,role});
      formElement.reset();
      setInviteStatus(`Invitation queued for ${email}.`);
    } catch {
      setAuthError("We could not prepare that invitation. Please try again.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleResendInvite(invite: FamilyInvite) {
    if (!profile?.householdId) return;
    setAuthBusy(true);
    setAuthError("");
    setInviteStatus("");
    try {
      await httpsCallable(functions, "resendFamilyInvitation")({ inviteId: invite.id });
      setInviteStatus(`A fresh invitation was sent to ${invite.email}.`);
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : "We could not resend that invitation. Please try again.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleCancelInvite(invite: FamilyInvite) {
    if (!window.confirm(`Cancel the invitation to ${invite.email}?`)) return;
    setAuthBusy(true);
    setAuthError("");
    setInviteStatus("");
    try {
      await httpsCallable(functions, "cancelFamilyInvitation")({ inviteId: invite.id });
      setInviteStatus(`Invitation cancelled for ${invite.email}.`);
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : "We could not cancel that invitation. Please try again.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleChangeMemberRole(invite: FamilyInvite, role: string) {
    if (!profile?.householdId || !invite.acceptedBy) return;
    setAuthBusy(true);
    setAuthError("");
    setInviteStatus("");
    try {
      await httpsCallable(functions, "updateFamilyMemberRole")({
        householdId: profile.householdId,
        memberUid: invite.acceptedBy,
        inviteId: invite.id,
        role
      });
      setInviteStatus(`${invite.email} is now ${familyRoleLabel(role)}.`);
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : "We could not update that family member's role.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleAcceptInvite() {
    if (!user || !profile || !incomingInvite?.id) return;
    setAuthBusy(true);
    setAuthError("");
    try {
      await user.reload();
      await user.getIdToken(true);
      if (!user.emailVerified) {
        await sendEmailVerification(user);
        setAuthError("Check your email to verify this account, then select Join again.");
        return;
      }
      const response = await httpsCallable<{ inviteId: string }, { householdId: string; householdName: string }>(functions, "acceptFamilyInvitation")({ inviteId: incomingInvite.id });
      const nextProfile = { ...profile, householdId: response.data.householdId, householdName: response.data.householdName };
      setProfile(nextProfile);
      setIncomingInvite(null);
      window.history.replaceState({}, "", window.location.pathname);
      setAuthOpen(false);
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : "We could not join the family. Please ask the owner to resend the invitation.");
    } finally {
      setAuthBusy(false);
    }
  }

  const visibleInvitations = invitations.filter((invite, index, rows) => invite.status !== "cancelled" && rows.findIndex((candidate) => candidate.status !== "cancelled" && candidate.status === invite.status && canonicalInviteEmail(candidate.email) === canonicalInviteEmail(invite.email)) === index);
  const familyName = profile?.firstName || user?.displayName?.split(" ")[0] || user?.email?.split("@")[0] || "Ben";
  const currentMember = householdMembers.find((member) => member.uid === user?.uid);
  const canManageFamily = currentMember?.role === "owner" || currentMember?.role === "admin";
  const financialMfaReady = hasTotpMfa(user);
  const availableNavItems = financialMfaReady
    ? navItems
    : navItems.filter((item) => !["Finances", "Expenses", "Income", "Investments"].includes(item.label));
  const needsProfile = Boolean(user && !profile?.firstName);
  const needsInviteAcceptance = Boolean(user && profile?.firstName && incomingInvite?.status === "pending");
  const needsFamily = Boolean(user && profile?.firstName && !profile.householdId && !needsInviteAcceptance);

  const time = useMemo(
    () => now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
    [now]
  );
  const date = useMemo(
    () => now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" }),
    [now]
  );
  const todayDateKey = localDateKey(now);
  const tideEvents = useMemo(() => {
    if (!ocean?.predictions?.length) return [];
    const sameDay = ocean.predictions.filter((event) => event.time.startsWith(todayDateKey));
    return (sameDay.length ? sameDay : ocean.predictions.slice(0, 4)).map((event) => {
      const date = new Date(event.time.replace(" ", "T"));
      return {
        label: event.type,
        time: date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
        height: event.height,
        minutes: date.getHours() * 60 + date.getMinutes()
      };
    });
  }, [todayDateKey, ocean]);
  const tidePosition = useMemo(() => {
    if (tideEvents.length < 2) return null;
    const minutes = now.getHours() * 60 + now.getMinutes();
    const progress = minutes / 1440;
    const nextIndex = tideEvents.findIndex((event) => event.minutes > minutes);
    const next = tideEvents[nextIndex < 0 ? 0 : nextIndex];
    const previous = tideEvents[(nextIndex <= 0 ? tideEvents.length : nextIndex) - 1];
    const startMinutes = previous.minutes > minutes ? previous.minutes - 1440 : previous.minutes;
    const endMinutes = next.minutes < minutes ? next.minutes + 1440 : next.minutes;
    const fraction = Math.max(0, Math.min(1, (minutes - startMinutes) / (endMinutes - startMinutes)));
    const eased = (1 - Math.cos(Math.PI * fraction)) / 2;
    const height = previous.height + (next.height - previous.height) * eased;
    return { progress, height, rising: next.label === "High", next };
  }, [now, tideEvents]);
  const daylight = useMemo(() => {
    const sunrise = weather?.sunrise ? new Date(weather.sunrise) : new Date(now.getFullYear(), now.getMonth(), now.getDate(), 6, 14);
    const sunset = weather?.sunset ? new Date(weather.sunset) : new Date(now.getFullYear(), now.getMonth(), now.getDate(), 19, 34);
    return { sunrise, sunset, progress: Math.max(0, Math.min(1, (now.getTime() - sunrise.getTime()) / (sunset.getTime() - sunrise.getTime()))) };
  }, [now, weather?.sunrise, weather?.sunset]);
  const moon = useMemo(() => moonInfo(now), [now]);
  const conditionHourBucket = Math.floor(now.getHours() / 4);
  const sunriseLabel = formatClock(daylight.sunrise.toISOString());
  const sunsetLabel = formatClock(daylight.sunset.toISOString());
  const skySeason = Math.floor(now.getMonth() / 3);
  const fallbackConditionInsights = useMemo<ConditionInsights>(() => {
    const tideInsight = tidePosition
      ? `The tide is ${tidePosition.rising ? "rising" : "falling"} toward a ${tidePosition.next.height.toFixed(1)} ft ${tidePosition.next.label.toLowerCase()} tide at ${tidePosition.next.time}, then the shoreline starts its next turn.`
      : "Live NOAA tide details are loading.";
    const sunMoonInsight = `Daylight runs from ${sunriseLabel} to ${sunsetLabel}. Tonight’s Moon is ${moon.phase.toLowerCase()} and ${moon.illumination}% illuminated.`;
    const seasonalSky = [
      "Look for Orion’s three-star belt and the bright stars that frame the winter sky.",
      "The Big Dipper is worth spotting tonight; follow its two outer bowl stars toward Polaris.",
      "Look for the bright Summer Triangle formed by Vega, Deneb, and Altair.",
      "Try finding Cassiopeia’s distinctive W shape in the northern sky."
    ][skySeason];
    const tonightSky = moon.days <= 2
      ? moon.days === 0 ? `${moon.nextPhase} is tonight.` : `${moon.nextPhase} arrives in ${moon.days} day${moon.days === 1 ? "" : "s"}.`
      : seasonalSky;
    const weatherOverview = weather
      ? `${weatherLabel(weather.weatherCode)} and ${weather.temperature}° now, with a high of ${weather.high}° and a low of ${weather.low}°.`
      : "Live weather details are loading.";
    const weatherUv = weather
      ? `Today’s UV index peaks at ${weather.uvIndex.toFixed(1)}, which is ${uvLabel(weather.uvIndex).toLowerCase()}.`
      : "UV details are loading.";
    const weatherWind = weather
      ? `Wind is ${weather.windSpeed} mph from the ${compassDirection(weather.windDirection)}, with gusts around ${weather.windGust} mph.`
      : "Wind details are loading.";
    const weatherPrecipitation = weather
      ? `There’s a ${weather.precipitationChance}% chance of precipitation, with ${weather.precipitationAmount.toFixed(2)} in forecast across about ${weather.precipitationHours} hour${weather.precipitationHours === 1 ? "" : "s"}.`
      : "Precipitation details are loading.";
    return { tideInsight, sunMoonInsight, tonightSky, weatherOverview, weatherUv, weatherWind, weatherPrecipitation, generated: false };
  }, [moon.days, moon.illumination, moon.nextPhase, moon.phase, skySeason, sunriseLabel, sunsetLabel, tidePosition, weather]);
  const conditionRequest = useMemo(() => {
    if (!profile?.householdId || !ocean || !weather || tideEvents.length < 2) return null;
    return {
      householdId: profile.householdId,
      dateKey: todayDateKey,
      hourBucket: conditionHourBucket,
      locationLabel: activeLocation.name,
      beachName: ocean.beach.name,
      tideStationName: ocean.beach.tideStationName,
      tides: ocean.predictions.slice(0, 8).map((tide) => ({ type: tide.type, time: tide.time, height: tide.height })),
      sunrise: sunriseLabel,
      sunset: sunsetLabel,
      moonPhase: moon.phase,
      moonIllumination: moon.illumination,
      nextMoonPhase: moon.nextPhase,
      daysToNextMoonPhase: moon.days,
      weather: {
        condition: weatherLabel(weather.weatherCode),
        temperature: weather.temperature,
        apparentTemperature: weather.apparentTemperature,
        high: weather.high,
        low: weather.low,
        relativeHumidity: weather.relativeHumidity,
        uvIndex: weather.uvIndex,
        windSpeed: weather.windSpeed,
        windGust: weather.windGust,
        windDirection: weather.windDirection,
        precipitationChance: weather.precipitationChance,
        precipitationAmount: weather.precipitationAmount,
        precipitationHours: weather.precipitationHours
      }
    };
  }, [activeLocation.name, conditionHourBucket, moon.days, moon.illumination, moon.nextPhase, moon.phase, ocean, profile?.householdId, sunriseLabel, sunsetLabel, tideEvents.length, todayDateKey, weather]);

  useEffect(() => {
    if (!user || !conditionRequest) {
      setConditionInsights(null);
      return;
    }
    let active = true;
    setConditionInsights(null);
    getConditionInsights(conditionRequest)
      .then((value) => { if (active) setConditionInsights(value); })
      .catch(() => { if (active) setConditionInsights(null); });
    return () => { active = false; };
  }, [conditionRequest, user]);

  const displayedConditionInsights = conditionInsights || fallbackConditionInsights;
  const weatherInsight = {
    overview: displayedConditionInsights.weatherOverview,
    uv: displayedConditionInsights.weatherUv,
    wind: displayedConditionInsights.weatherWind,
    precipitation: displayedConditionInsights.weatherPrecipitation
  }[weatherView];
  const upcomingEvents = useMemo(
    () => sortUpcomingEvents(calendarEvents, now),
    [calendarEvents, now]
  );
  const homeTodayEvents = useMemo(
    () => eventsForCalendarDay(calendarEvents, now),
    [calendarEvents, now]
  );
  const homepageCountdowns = upcomingEvents
    .filter((event) => event.showOnHome)
    .slice(0, 4);
  const availableCalendarEvents = upcomingEvents
    .filter((event) => !event.showOnHome)
    .slice(0, 6);
  const writableConnections = writableCalendarConnections(calendarConnections);
  const calendarLocationSuggestions = useMemo(
    () => [...new Set(calendarEvents.map((event) => event.location?.trim()).filter((value): value is string => Boolean(value)))].slice(0, 20),
    [calendarEvents]
  );
  const activeShoppingItems = listItems.filter((item) => item.kind === "shopping" && !item.done);
  const activeHouseItems = listItems.filter((item) => isAvailableHouseTask(item, user?.uid));

  return (
    <div className="dashboard-shell" data-release="firebase-auth-v1">
      {!authReady && <div className="auth-restoring" role="status" aria-live="polite"><span aria-hidden="true">⌂</span><strong>FamDashFun</strong><small>Opening your dashboard…</small></div>}
      {authReady && !user && (
        <MarketingLanding
          onSignIn={() => { setAuthError(""); setAuthNotice(""); setAuthMode("signin"); setAuthOpen(true); }}
          onCreateAccount={() => { setAuthError(""); setAuthNotice(""); setAuthMode("signup"); setAuthOpen(true); }}
        />
      )}

      {user && (
        <>
      <header className="dashboard-header">
        <nav className="top-nav" aria-label="Family Dashboard sections">
          {availableNavItems.map((item) => (
            <button
              type="button"
              key={item.label}
              className={activeNav === item.label ? "top-nav-item active" : "top-nav-item"}
              onClick={() => setActiveNav(item.label)}
            >
              <span className="top-nav-icon" aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="header-utilities" aria-label="Dashboard tools">
          <button type="button" className="utility-button desktop-utility search-utility" onClick={() => { setMoreOpen(false); setSearchOpen(true); }} aria-label="Search and ask FamDashFun">
            <span aria-hidden="true">⌕</span><b>Search / AI</b>
          </button>
          <button type="button" className="utility-button desktop-utility account-utility" onClick={() => { setMoreOpen(false); setAuthError(""); setAuthOpen(true); }} aria-label={user ? `Open ${familyName}'s settings` : "Sign in"}>
            {user && <AvatarPortrait avatar={currentMember?.avatar} className="header-profile-avatar" />}
            <b>{user ? familyName : "Sign in"}</b>
          </button>
          {user&&<NotificationBell uid={user.uid}/>}
          <div className="more-menu mobile-utility">
            <button type="button" className="utility-button utility-icon-only" aria-label="Open search and settings" aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen((open) => !open)}>
              <span aria-hidden="true">•••</span>
            </button>
            {moreOpen && (
              <div className="more-menu-popover" role="menu">
                <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); setSearchOpen(true); }}><span aria-hidden="true">⌕</span>Search / AI</button>
                <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); setAuthError(""); setAuthOpen(true); }}><span aria-hidden="true">⚙</span>{user ? `${familyName}'s settings` : "Sign in"}</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="dashboard">
        {user && profile?.householdId && <FamilyBeaconAlert householdId={profile.householdId} userUid={user.uid} members={householdMembers} />}
        {activeNav !== "Home" && activeNav !== "Calendar" && activeNav !== "Lists" && activeNav !== "Family Book" && activeNav !== "Health" && !["Finances", "Expenses", "Income", "Investments"].includes(activeNav) && (
          <div className="migration-notice" role="status">
            <strong>{activeNav}</strong> is queued for migration. Home is the first design-complete React page.
            <button type="button" onClick={() => setActiveNav("Home")}>Back home</button>
          </div>
        )}

        {(["Finances", "Expenses", "Income", "Investments"] as const).includes(activeNav as "Finances" | "Expenses" | "Income" | "Investments") && (
          <FinanceScreen key={`${user?.uid}:${profile?.householdId}`} section={activeNav as "Finances" | "Expenses" | "Income" | "Investments"} members={householdMembers} householdId={profile?.householdId || ""} onOpenFinanceSettings={() => { setSettingsSection("finance"); setAuthOpen(true); }} />
        )}

        {activeNav === "Calendar" && (
          <CalendarScreen
            events={calendarEvents}
            members={householdMembers}
            onAdd={(dateKey) => openEventEditor(null, dateKey, false)}
            onEdit={(event) => openEventEditor(event, event.dateKey, event.showOnHome)}
            onOpenConnections={openCalendarConnections}
            familyPulse={{ headline: homeAiContent.pulseHeadline, body: homeAiContent.pulseBody, source: homeAiContent.generated ? "Gemini generated" : "Firebase connected" }}
          />
        )}

        {activeNav === "Lists" && user && profile?.householdId && (
          <ListsScreen
            householdId={profile.householdId}
            userUid={user.uid}
            items={listItems}
            members={householdMembers}
            activeMode={activeListMode}
            onModeChange={setActiveListMode}
          />
        )}

        {activeNav === "Lists" && (!user || !profile?.householdId) && (
          <div className="migration-notice" role="status">Sign in to open your shared family lists.<button type="button" onClick={() => setAuthOpen(true)}>Sign in</button></div>
        )}

        {activeNav === "Family Book" && user && profile?.householdId && (
          <FamilyBookScreen householdId={profile.householdId} userUid={user.uid} firstName={profile.firstName || user.displayName || "you"} />
        )}

        {activeNav === "Family Book" && (!user || !profile?.householdId) && (
          <div className="migration-notice" role="status">Sign in to open your private Family Book.<button type="button" onClick={() => setAuthOpen(true)}>Sign in</button></div>
        )}

        {activeNav === "Health" && user && profile?.householdId && (
          <HealthScreen key={`${profile.householdId}:${user.uid}`} householdId={profile.householdId} userUid={user.uid} members={householdMembers} />
        )}
        {activeNav === "Health" && (!user || !profile?.householdId) && <div className="migration-notice">Sign in to open your personal Health tab.</div>}

        {activeNav === "Home" && (
        <section className="panel home-hub" aria-labelledby="greeting" data-home-hunt-view="Home:main">
          <header className="home-hero">
            <div>
              <p className="eyebrow">Our family dashboard</p>
              <h1 id="greeting">{getGreeting(now.getHours())}, {familyName}</h1>
              <p className="message">Here’s what’s happening with the family today.</p>
            </div>
            <div className="home-hero-actions">
              <div className="home-clock">
                <strong>{time}</strong>
                <span>{date}</span>
              </div>
            </div>
          </header>

          <blockquote className="home-quote">
            <span aria-hidden="true">“</span>
            <p>{homeAiContent.quote}</p>
            <cite>— {homeAiContent.attribution}</cite>
          </blockquote>

          {user && profile?.householdId ? (
            <FamilyCheckInCard householdId={profile.householdId} currentUid={user.uid} members={householdMembers} />
          ) : (
            <article className="home-card family-checkin-card">
              <div className="card-heading"><div><span>Stay connected, not tracked</span><h3>Family Ping</h3></div></div>
              <p className="checkin-intro">Sign in to send a note, check in, or ask your family for a quick update.</p>
              <button type="button" className="primary-action" onClick={() => setAuthOpen(true)}>Sign in</button>
            </article>
          )}

          <article className="home-card home-today" data-widget-id="family-calendar">
            <CardHeading label="Family calendar" title="Today" action={`${homeTodayEvents.length} ${homeTodayEvents.length === 1 ? "plan" : "plans"}`} plus onAdd={() => setActiveNav("Calendar")} />
            {homeTodayEvents.length ? (
              <div className="home-card-list">
                {homeTodayEvents.map((event) => (
                  <button type="button" className="home-list-row home-calendar-row" key={event.id} onClick={() => setActiveNav("Calendar")}>
                    <time>{event.time ? new Date(`2000-01-01T${event.time}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "All day"}</time>
                    <div><strong>{event.title}</strong><small>{event.location || (event.source === "manual" ? "Family calendar" : `${event.source === "google" ? "Google" : "Apple"} calendar`)}</small></div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="home-calendar-empty">
                <p>Nothing has been added for today.</p>
                <button type="button" onClick={() => setActiveNav("Calendar")}>OPEN FAMILY CALENDAR →</button>
              </div>
            )}
          </article>

          {user && profile?.householdId ? (
            <FamilyGamesWidget householdId={profile.householdId} userUid={user.uid} members={householdMembers} />
          ) : (
            <section className="family-games-shell"><article className="home-card game-signin-card"><p className="leader-label">Family games</p><h3>Sign in to play today’s Face-Off</h3><button type="button" className="primary-action" onClick={() => setAuthOpen(true)}>Sign in</button></article></section>
          )}

          <div className="home-grid">
            <article className="home-card weather-card story-widget" data-widget-id="weather">
              <div className="home-card-heading weather-heading">
                <div><p className="leader-label">{activeLocation.name}</p><h3>Weather</h3></div>
                <label className="weather-view-picker">
                  <span className="sr-only">Weather detail</span>
                  <select value={weatherView} onChange={(event) => setWeatherView(event.target.value as WeatherView)} aria-label="Choose weather detail">
                    <option value="overview">Overview</option>
                    <option value="uv">UV index</option>
                    <option value="wind">Wind</option>
                    <option value="precipitation">Precipitation</option>
                  </select>
                </label>
              </div>
              <div className="condition-lead weather-lead">
                <button type="button" className="location-shortcut" onClick={() => { setSettingsSection("location"); setAuthError(""); setAuthOpen(true); }}>Change location</button>
              </div>
              {weather ? <>
                {weatherView === "overview" && <>
                  <WeatherHourlyChart weather={weather} view={weatherView} now={now} />
                  <div className="weather-details">
                    <MiniEvent label="High / Low" time={`${weather.high}° / ${weather.low}°`} value="Today" />
                    <MiniEvent label="Humidity" time={`${weather.relativeHumidity}%`} value={`Wind ${weather.windSpeed} mph`} />
                  </div>
                </>}
                {weatherView === "uv" && <>
                  <WeatherHourlyChart weather={weather} view={weatherView} now={now} />
                  <div className="weather-details"><MiniEvent label="UV level" time={uvLabel(weather.uvIndex)} value="Daily max" /><MiniEvent label="Sky" time={weatherLabel(weather.weatherCode)} value={`${weather.temperature}° now`} /></div>
                </>}
                {weatherView === "wind" && <>
                  <WeatherHourlyChart weather={weather} view={weatherView} now={now} />
                  <div className="weather-details"><MiniEvent label="Direction" time={`${compassDirection(weather.windDirection)} · ${weather.windDirection}°`} value="Current" /><MiniEvent label="Gusts" time={`${weather.windGust} mph`} value="Current" /></div>
                </>}
                {weatherView === "precipitation" && <>
                  <WeatherHourlyChart weather={weather} view={weatherView} now={now} />
                  <div className="weather-details"><MiniEvent label="Forecast amount" time={`${weather.precipitationAmount.toFixed(2)} in`} value="Today" /><MiniEvent label="Expected duration" time={`${weather.precipitationHours} hr`} value="Today" /></div>
                </>}
                <div className="tonights-sky weather-summary"><strong>Weather take</strong><span>{weatherInsight}</span></div>
              </> : <p className="widget-status">{weatherStatus}</p>}
              <footer className="condition-source">Open-Meteo forecast · local timezone</footer>
            </article>

            <div className="quick-list-stack">
              <article className="home-card compact-list-card shopping-widget" data-widget-id="shopping-list">
                <CardHeading label="Shared list" title="Shopping" action={`${activeShoppingItems.length} ${activeShoppingItems.length === 1 ? "item" : "items"}`} plus onAdd={() => openLists("shopping")} onOpen={() => openLists("shopping")} />
                <button type="button" className="compact-list-cta" onClick={() => openLists("shopping")}>
                  <strong>{homeAiContent.shoppingBlurb || (shoppingHasItems ? "Everyone’s favorite thing: shopping. The list awaits." : "Doesn’t anyone like spending money around here?")}</strong>
                  <span>{activeShoppingItems.length ? "Open shopping list" : "The list is empty—for now"} →</span>
                </button>
              </article>

              <article className="home-card compact-list-card house-widget" data-widget-id="house-tasks">
                <CardHeading label="Next up" title="Around the House" action={`${activeHouseItems.length} ${activeHouseItems.length === 1 ? "task" : "tasks"}`} plus onAdd={() => openLists("house")} onOpen={() => openLists("house")} />
                <button type="button" className="compact-list-cta" onClick={() => openLists("house")}>
                  <strong>{houseHasItems ? (homeAiContent.houseBlurb || "The house has made another formal request.") : "You’re all caught up. Nothing assigned to you or Anyone."}</strong>
                  <span>{activeHouseItems.length ? "Open house list" : "Miraculously, nothing is waiting"} →</span>
                </button>
              </article>
            </div>

            <div className="conditions-story-grid">
              <article className="home-card tide-card story-widget" data-widget-id="tides">
                <div className="home-card-heading tide-heading">
                  <div className="tide-title-block">
                    <p className="leader-label">{ocean?.beach.name || "Selected beach"}</p>
                    <div className="tide-title-row">
                      <h3>Today’s tides</h3>
                    <div className="ocean-mode-toggle" aria-label="Tide location">
                      <button type="button" className={oceanMode === "home" ? "active" : ""} onClick={chooseOceanHomeBeach}>Home beach</button>
                      <button type="button" className={oceanMode === "current" ? "active" : ""} onClick={chooseOceanCurrentLocation}>Near me</button>
                    </div>
                    </div>
                  </div>
                  <div className="tide-heading-actions"><span>{tidePosition ? `Now ${tidePosition.height.toFixed(1)} ft · ${tidePosition.rising ? "rising" : "falling"}` : "Loading live NOAA data…"}</span></div>
                </div>
                <div className="condition-lead tide-lead">
                  {ocean?.beach.usedFallback && <p className="compact-condition-status">Showing Poche Beach because no nearby coastal station was found.</p>}
                  {tidePosition && <p className="condition-insight">{displayedConditionInsights.tideInsight}</p>}
                </div>
                {tidePosition && <div className="tide-chart rich-tide-chart" aria-label={`Tide is ${tidePosition.height.toFixed(1)} feet and ${tidePosition.rising ? "rising" : "falling"}`}>
                  <svg viewBox="0 0 500 150" role="img">
                    <path className="tide-grid" d="M30 25H470 M30 75H470 M30 125H470 M30 25V125 M140 25V125 M250 25V125 M360 25V125 M470 25V125" />
                    <path className="water-fill" d="M30 72 C85 38 115 112 175 98 S255 28 315 48 S400 122 470 90 V125 H30Z" />
                    <path className="water-line" d="M30 72 C85 38 115 112 175 98 S255 28 315 48 S400 122 470 90" />
                    <line className="tide-now-line" x1={30 + tidePosition.progress * 440} y1="25" x2={30 + tidePosition.progress * 440} y2="125" />
                    <circle className="tide-now-dot" cx={30 + tidePosition.progress * 440} cy={125 - ((tidePosition.height - .5) / 5) * 100} r="7" />
                    <g className="chart-axis"><text x="30" y="145">12am</text><text x="140" y="145">6am</text><text x="250" y="145">12pm</text><text x="360" y="145">6pm</text><text x="448" y="145">12am</text></g>
                  </svg>
                </div>}
                <div className="condition-events tide-events">
                  {tideEvents.map((event) => <MiniEvent key={event.time} label={`${event.label === "High" ? "↑" : "↓"} ${event.label}`} time={event.time} value={`${event.height.toFixed(1)} ft`} />)}
                </div>
                {ocean?.buoy && <div className="tonights-sky"><strong>Ocean now</strong><span>{ocean.buoy.waveHeightFt != null ? `${ocean.buoy.waveHeightFt} ft waves` : "Wave height unavailable"}{ocean.buoy.dominantPeriodSeconds != null ? ` · ${ocean.buoy.dominantPeriodSeconds}s period` : ""}{ocean.buoy.waterTemperatureF != null ? ` · ${ocean.buoy.waterTemperatureF}° water` : ""}</span></div>}
                {oceanStatus && <p className="widget-status">{oceanStatus}</p>}
                <footer className="condition-source">{ocean ? `${ocean.beach.name} · NOAA tide station ${ocean.beach.tideStationName} · buoy ${ocean.beach.buoy}` : "NOAA ocean conditions"}</footer>
              </article>

              <article className="home-card sky-card story-widget" data-widget-id="sun-moon">
                <CardHeading label={`${activeLocation.name} sky`} title="Sun & Moon" action={`◐ ${moon.phase} · ${moon.illumination}%`} />
                <div className="condition-lead sky-lead">
                  <p className="condition-insight">{displayedConditionInsights.sunMoonInsight}</p>
                </div>
                <div className="sun-journey" aria-label="Current position of the sun between sunrise and sunset">
                  <svg viewBox="0 0 500 150" role="img">
                    <defs><linearGradient id="skyGradient" x1="0" x2="1"><stop offset="0" stopColor="#f7c960" /><stop offset=".52" stopColor="#78c6df" /><stop offset="1" stopColor="#6878aa" /></linearGradient><linearGradient id="arcGradient"><stop offset="0" stopColor="#f59b43" /><stop offset=".5" stopColor="#ffe37c" /><stop offset="1" stopColor="#7652a4" /></linearGradient></defs>
                    <rect width="500" height="150" rx="14" fill="url(#skyGradient)" />
                    <path className="sun-path" d="M60 120 Q250 0 440 120" />
                    <line className="horizon-line" x1="28" y1="122" x2="472" y2="122" />
                    <circle className="sun-position" cx={60 + 380 * daylight.progress} cy={120 - 120 * 4 * daylight.progress * (1 - daylight.progress)} r="10" />
                    <g className="sky-axis"><text x="44" y="143">Sunrise</text><text x="232" y="143">Noon</text><text x="421" y="143">Sunset</text></g>
                  </svg>
                </div>
                <div className="condition-events sky-events">
                  <MiniEvent label="☀ Sunrise" time={formatClock(daylight.sunrise.toISOString())} value="Morning" />
                  <MiniEvent label="☀ Sunset" time={formatClock(daylight.sunset.toISOString())} value="Evening" />
                  <MiniEvent label="◐ Moon" time={`${moon.illumination}% lit`} value={moon.waxing ? "Waxing" : "Waning"} />
                  <MiniEvent label="Next phase" time={moon.nextPhase} value={`${moon.days}d`} />
                </div>
                <div className="tonights-sky"><strong>Tonight’s sky</strong><span>{displayedConditionInsights.tonightSky}</span></div>
                <footer className="condition-source">{activeLocation.name} astronomical times · local timezone</footer>
              </article>
            </div>

            <article className="home-card countdown-card" data-widget-id="countdowns">
              <CardHeading
                label="Something to look forward to"
                title="Family countdowns"
                action={homepageCountdowns.length ? `${homepageCountdowns.length} upcoming` : "Add the first one"}
                plus
                onAdd={() => openEventEditor()}
              />
              {homepageCountdowns.length ? (
                <div className="countdown-grid">
                  {homepageCountdowns.map((event) => {
                    const countdown = countdownValue(event, now);
                    return (
                      <Countdown
                        key={event.id}
                        name={event.title}
                        value={countdown.value}
                        unit={countdown.unit}
                        onClick={() => openEventEditor(event)}
                      />
                    );
                  })}
                </div>
              ) : (
                <button type="button" className="countdown-empty" onClick={() => openEventEditor()}>
                  Add a birthday, trip, milestone, or any calendar event worth counting down to.
                </button>
              )}
              {upcomingEvents.filter((event) => event.showOnHome).length > 4 && (
                <button type="button" className="countdown-view-all" onClick={() => openEventEditor()}>
                  View all countdowns
                </button>
              )}
            </article>

          </div>
        </section>
        )}
      </main>

      {countdownOpen && (
        <div className="auth-backdrop" role="presentation" onMouseDown={() => setCountdownOpen(false)}>
          <section className="auth-dialog countdown-dialog calendar-event-dialog" role="dialog" aria-modal="true" aria-labelledby="countdown-title" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" className="auth-close" aria-label="Close calendar event" onClick={() => setCountdownOpen(false)}>×</button>
            <p className="widget-eyebrow">Family calendar</p>
            <h2 id="countdown-title">{selectedCalendarEvent ? selectedCalendarEvent.source === "manual" ? "Edit event" : "Event details" : "Add event"}</h2>
            {selectedCalendarEvent?.source !== "manual" && selectedCalendarEvent ? (
              <div className="calendar-imported-details">
                <p className="auth-copy">This event comes from {selectedCalendarEvent.source === "google" ? "Google Calendar" : "Apple Calendar"}. Edit it there, then sync the connection.</p>
                <dl>
                  <div><dt>When</dt><dd>{new Date(`${selectedCalendarEvent.dateKey}T12:00:00`).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}{selectedCalendarEvent.time ? ` · ${new Date(`2000-01-01T${selectedCalendarEvent.time}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : " · All day"}</dd></div>
                  {selectedCalendarEvent.location && <div><dt>Location</dt><dd>{selectedCalendarEvent.location}</dd></div>}
                  {selectedCalendarEvent.details && <div><dt>Details</dt><dd>{selectedCalendarEvent.details}</dd></div>}
                </dl>
                {selectedCalendarEvent.location && <a className="secondary-action calendar-map-link" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedCalendarEvent.location)}`} target="_blank" rel="noreferrer">Open in Maps</a>}
              </div>
            ) : (
              <form className="auth-form countdown-form" key={selectedCalendarEvent?.id || "new"} onSubmit={handleSaveCalendarEvent}>
                <label>Event title
                  <input name="title" maxLength={80} defaultValue={selectedCalendarEvent?.title || ""} placeholder="What’s happening?" required autoFocus />
                </label>
                <label className="calendar-all-day">
                  <input name="allDay" type="checkbox" checked={eventEditorAllDay} onChange={(event) => setEventEditorAllDay(event.target.checked)} />
                  <span>All-day event</span>
                </label>
                <div className="calendar-form-grid">
                  <label>Starts
                    <input name="dateKey" type="date" defaultValue={selectedCalendarEvent?.dateKey || eventEditorDate} required />
                  </label>
                  <label>Ends <span className="optional-label">Optional</span>
                    <input name="endDateKey" type="date" defaultValue={selectedCalendarEvent?.endDateKey || ""} min={selectedCalendarEvent?.dateKey || eventEditorDate} />
                  </label>
                </div>
                {!eventEditorAllDay && <div className="calendar-form-grid">
                  <label>Start time
                    <input name="time" type="time" defaultValue={selectedCalendarEvent?.time || ""} required />
                  </label>
                  <label>End time <span className="optional-label">Optional</span>
                    <input name="endTime" type="time" defaultValue={selectedCalendarEvent?.endTime || ""} />
                  </label>
                </div>}
                <label>Repeats
                  <select name="repeat" defaultValue={selectedCalendarEvent?.repeat === "recurring" ? "none" : selectedCalendarEvent?.repeat || "none"}>
                    <option value="none">Does not repeat</option><option value="daily">Every day</option><option value="weekly">Every week</option><option value="monthly">Every month</option><option value="yearly">Every year</option>
                  </select>
                </label>
                <label>Location or address <span className="optional-label">Optional</span>
                  <input name="location" maxLength={240} list="calendar-location-suggestions" defaultValue={selectedCalendarEvent?.location || ""} placeholder="123 Main St or Soccer field" />
                </label>
                <datalist id="calendar-location-suggestions">{calendarLocationSuggestions.map((location) => <option value={location} key={location} />)}</datalist>
                {selectedCalendarEvent?.location && <a className="calendar-inline-map" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedCalendarEvent.location)}`} target="_blank" rel="noreferrer">Open this address in Maps →</a>}
                <fieldset className="calendar-people-fieldset">
                  <legend>Who’s included?</legend>
                  <div>{householdMembers.map((member) => <label key={member.uid}><input name="participantUids" type="checkbox" value={member.uid} defaultChecked={!selectedCalendarEvent?.participantUids?.length || selectedCalendarEvent.participantUids.includes(member.uid)} /><span>{member.firstName}</span></label>)}</div>
                </fieldset>
                <label>Details <span className="optional-label">Optional</span>
                  <textarea name="details" maxLength={2000} defaultValue={selectedCalendarEvent?.details || ""} placeholder="Anything the family should know" rows={3} />
                </label>
                <details className="calendar-more-options">
                  <summary>More options</summary>
                  <label className="countdown-check">
                    <input name="showOnHome" type="checkbox" defaultChecked={eventEditorCountdownDefault} />
                    <span><strong>Show in family countdowns</strong><small>Useful for birthdays, trips, and big moments</small></span>
                  </label>
                  {writableConnections.length > 0 && <div className="calendar-routing">
                    <label>Also add a copy to
                      <select value={destinationConnectionIds[0] || ""} onChange={(event) => setDestinationConnectionIds(event.target.value ? [event.target.value] : [])}>
                        <option value="">Family calendar only</option>{writableConnections.map((connection) => <option value={connection.id} key={connection.id}>{connection.displayName}</option>)}
                      </select>
                    </label>
                    {destinationConnectionIds[0] && <>
                      <input type="hidden" name="destinationTitle" value={selectedCalendarEvent?.title || ""} />
                      <label className="countdown-check"><input name="destinationBusy" type="checkbox" defaultChecked /><span><strong>Mark me busy</strong></span></label>
                      <label className="countdown-check"><input name="keepSynchronized" type="checkbox" defaultChecked /><span><strong>Keep synchronized</strong></span></label>
                      <label className="countdown-check"><input name="includeDestinationDetails" type="checkbox" /><span><strong>Include family details</strong></span></label>
                    </>}
                  </div>}
                </details>
                {countdownStatus && <p className={countdownStatus.includes("couldn’t") ? "auth-error" : "auth-success"} role="status">{countdownStatus}</p>}
                <div className="calendar-form-actions">
                  <button className="auth-submit" type="submit" disabled={countdownBusy}>{countdownBusy ? "Saving…" : selectedCalendarEvent ? "Save changes" : "Add event"}</button>
                  {selectedCalendarEvent && <button type="button" className="calendar-delete-event" disabled={countdownBusy} onClick={() => handleDeleteCalendarEvent(selectedCalendarEvent)}>Delete event</button>}
                </div>
              </form>
            )}
          </section>
        </div>
      )}

      {searchOpen && (
        <div className="search-backdrop" role="presentation" onMouseDown={() => setSearchOpen(false)}>
          <section className="search-dialog" role="dialog" aria-modal="true" aria-labelledby="search-title" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" className="auth-close" aria-label="Close search" onClick={() => setSearchOpen(false)}>×</button>
            <p className="eyebrow">Find anything</p>
            <h2 id="search-title">Search FamDashFun</h2>
            <label className="dashboard-search">
              <span aria-hidden="true">⌕</span>
              <input type="search" placeholder="Search the dashboard or ask a question…" autoFocus />
            </label>
            <p className="search-note">Dashboard search comes first. This same space is ready for helpful AI questions when we add that feature.</p>
          </section>
        </div>
      )}

        </>
      )}

      {user && <HomeHunt householdId={profile?.householdId} userUid={user.uid} activeView={activeNav} />}

      {authOpen && (
        <div className="auth-backdrop" role="presentation" onMouseDown={closeSettings}>
          <section className={user && profile?.householdId ? "auth-dialog settings-dialog" : "auth-dialog"} role="dialog" aria-modal="true" aria-labelledby="auth-title" onMouseDown={(event) => event.stopPropagation()}>
            {!needsProfile && !needsFamily && !needsInviteAcceptance && <button type="button" className="auth-close settings-close" aria-label="Close settings" onPointerDown={(event) => { event.preventDefault(); event.stopPropagation(); closeSettings(); }} onClick={closeSettings}>×</button>}
            <p className="eyebrow">Family access</p>

            {!user && mfaResolver && (
              <>
                <h2 id="auth-title">Enter your authenticator code</h2>
                <form className="auth-form" onSubmit={handleMfaSignIn}>
                  <p className="auth-copy">Open your authenticator app and enter the current six-digit FamDashFun code.</p>
                  <label>Six-digit code<input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={mfaCode} onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, "").slice(0, 6))} required autoFocus /></label>
                  {authError && <p className="auth-error" role="alert">{authError}</p>}
                  <button className="auth-submit" type="submit" disabled={authBusy || mfaCode.length !== 6}>{authBusy ? "Checking…" : "Finish signing in"}</button>
                  <button className="auth-link" type="button" onClick={() => { setMfaResolver(null); setMfaCode(""); setAuthError(""); }}>Use a different account</button>
                </form>
              </>
            )}

            {!user && !mfaResolver && authMode === "signin" && (
              <>
                <h2 id="auth-title">Sign in to the dashboard</h2>
                <form className="auth-form" onSubmit={handleSignIn}>
                  <p className="auth-copy">Use your personal family-dashboard account.</p>
                  <button className="google-signin" type="button" disabled={authBusy} onClick={handleGoogleSignIn}><span aria-hidden="true">G</span> Continue with Google</button>
                  <div className="auth-divider"><span>or use email</span></div>
                  <label>Email<input name="email" type="email" autoComplete="email" required /></label>
                  <label>Password<input name="password" type="password" autoComplete="current-password" required /></label>
                  {authError && <p className="auth-error" role="alert">{authError}</p>}
                  <button className="auth-submit" type="submit" disabled={authBusy}>{authBusy ? "Signing in…" : "Sign in"}</button>
                  <button className="auth-link" type="button" onClick={() => { setAuthError(""); setAuthNotice(""); setAuthMode("reset"); }}>Forgot password?</button>
                  <button className="auth-link" type="button" onClick={() => { setAuthError(""); setAuthMode("signup"); }}>New here? Create an account</button>
                </form>
              </>
            )}

            {!user && authMode === "reset" && (
              <>
                <h2 id="auth-title">Reset your password</h2>
                <form className="auth-form" onSubmit={handlePasswordReset}>
                  <p className="auth-copy">Enter the email used for your family-dashboard account and Firebase will send a secure reset link.</p>
                  <label>Email<input name="email" type="email" autoComplete="email" required autoFocus /></label>
                  {authError && <p className="auth-error" role="alert">{authError}</p>}
                  {authNotice && <p className="auth-success" role="status">{authNotice}</p>}
                  <button className="auth-submit" type="submit" disabled={authBusy}>{authBusy ? "Sending…" : "Send reset link"}</button>
                  <button className="auth-link" type="button" onClick={() => { setAuthError(""); setAuthNotice(""); setAuthMode("signin"); }}>Back to sign in</button>
                </form>
              </>
            )}

            {!user && authMode === "signup" && (
              <>
                <h2 id="auth-title">Create your account</h2>
                <form className="auth-form" onSubmit={handleCreateAccount}>
                  <p className="auth-copy">{incomingInvite ? `Create your account to join ${incomingInvite.householdName}.` : "Start with your own account. You’ll create your family next."}</p>
                  {incomingInvite && <><button className="google-signin" type="button" disabled={authBusy} onClick={handleGoogleSignIn}><span aria-hidden="true">G</span> Continue with Google</button><div className="auth-divider"><span>or create a password</span></div></>}
                  <div className="auth-name-grid">
                    <label>First name<input name="firstName" autoComplete="given-name" required /></label>
                    <label>Last name<input name="lastName" autoComplete="family-name" required /></label>
                  </div>
                  <label>Email<input name="email" type="email" autoComplete="email" defaultValue="" required /></label>
                  <label>Password<input name="password" type="password" minLength={6} autoComplete="new-password" required /></label>
                  {authError && <p className="auth-error" role="alert">{authError}</p>}
                  <button className="auth-submit" type="submit" disabled={authBusy}>{authBusy ? "Creating account…" : "Create account"}</button>
                  <button className="auth-link" type="button" onClick={() => { setAuthError(""); setAuthMode("signin"); }}>Already have an account? Sign in</button>
                  <button className="auth-link" type="button" onClick={() => { setAuthError(""); setAuthNotice(""); setAuthMode("reset"); }}>Forgot your password?</button>
                </form>
              </>
            )}

            {needsProfile && (
              <>
                <h2 id="auth-title">Complete your profile</h2>
                <form className="auth-form" onSubmit={handleCompleteProfile}>
                  <p className="auth-copy">Add your real name so greetings, tasks, games, and notifications identify you correctly.</p>
                  <div className="auth-name-grid">
                    <label>First name<input name="firstName" autoComplete="given-name" defaultValue={user?.displayName?.split(" ")[0] || ""} required autoFocus /></label>
                    <label>Last name<input name="lastName" autoComplete="family-name" defaultValue={user?.displayName?.split(" ").slice(1).join(" ") || ""} required /></label>
                  </div>
                  {authError && <p className="auth-error" role="alert">{authError}</p>}
                  <button className="auth-submit" type="submit" disabled={authBusy}>{authBusy ? "Saving…" : "Continue"}</button>
                </form>
              </>
            )}

            {needsInviteAcceptance && incomingInvite && (
              <>
                <h2 id="auth-title">Join {incomingInvite.householdName}</h2>
                <p className="auth-copy">You were invited as <strong>{incomingInvite.role === "admin" ? "Parent / Admin" : incomingInvite.role === "child" ? "Child" : "Family member"}</strong> using <strong>{incomingInvite.email}</strong>.</p>
                {authError && <p className="auth-error" role="alert">{authError}</p>}
                <button className="auth-submit" type="button" disabled={authBusy} onClick={handleAcceptInvite}>{authBusy ? "Joining…" : `Join ${incomingInvite.householdName}`}</button>
                <button type="button" className="auth-link auth-signout" onClick={async () => { try { await detachPushBeforeSignOut(); await signOut(auth); setAuthMode("signin"); } catch { setAuthError("Could not detach this device’s alerts. Please retry sign out."); } }}>Use a different account</button>
              </>
            )}

            {needsFamily && (
              <>
                <h2 id="auth-title">Create your family</h2>
                <form className="auth-form" onSubmit={handleCreateFamily}>
                  <p className="auth-copy">This private household keeps your family’s widgets, calendar, games, lists, and finances together.</p>
                  <label>Family name<input name="householdName" defaultValue="" placeholder="Family name" required autoFocus /></label>
                  {authError && <p className="auth-error" role="alert">{authError}</p>}
                  <button className="auth-submit" type="submit" disabled={authBusy}>{authBusy ? "Creating family…" : "Create family"}</button>
                </form>
              </>
            )}

            {user && profile?.householdId && (
              <>
                <h2 id="auth-title">Settings</h2>
                <p className="auth-copy">Hi, {familyName}. Manage {profile.householdName} and this device.</p>
                <div className="settings-sections">
                  <section className={settingsSection === "location" ? "settings-section open" : "settings-section"}>
                    <button type="button" className="settings-section-toggle" aria-expanded={settingsSection === "location"} onClick={() => setSettingsSection(settingsSection === "location" ? null : "location")}>
                      <span><b>Location</b><small>{locationMode === "current" ? "My current location" : householdLocation.name}</small></span><i aria-hidden="true">⌄</i>
                    </button>
                    {settingsSection === "location" && <div className="settings-section-panel">
                      <p className="auth-copy">Weather, tides, and sunlight use one location on this device. Current location is requested only when you choose it.</p>
                      <div className="location-options">
                        <button type="button" className={locationMode === "household" ? "active" : ""} onClick={chooseHouseholdLocation}><strong>Household</strong><span>{householdLocation.name}</span></button>
                        <button type="button" className={locationMode === "current" ? "active" : ""} onClick={chooseCurrentLocation}><strong>My current location</strong><span>{deviceLocation ? "Location available" : "Ask this device"}</span></button>
                      </div>
                      <form className="auth-form location-form" onSubmit={handleSaveHouseholdLocation}>
                        <label>Change household location<input name="locationQuery" placeholder="City or ZIP code" /></label>
                        <button className="auth-submit" type="submit" disabled={authBusy}>{authBusy ? "Updating…" : "Update household location"}</button>
                      </form>
                      <div className="auth-form location-form">
                        <label>Beach for tides and ocean conditions
                          <select value={beachSlug} onChange={(event) => {
                            const next = event.target.value;
                            setBeachSlug(next);
                            window.localStorage.setItem("famdashfun.beachSlug", next);
                            setLocationStatus(`${BEACH_OPTIONS.find((beach) => beach.slug === next)?.name || "Beach"} is now used for tides and buoy conditions.`);
                          }}>
                            {BEACH_OPTIONS.map((beach) => <option key={beach.slug} value={beach.slug}>{beach.name}</option>)}
                          </select>
                        </label>
                        <small>We automatically pair the beach with its nearest NOAA tide station and reporting buoy.</small>
                      </div>
                      {locationStatus && <p className="location-status" role="status">{locationStatus}</p>}
                    </div>}
                  </section>


                  <section className={settingsSection === "finance" ? "settings-section open" : "settings-section"}>
                    <button type="button" className="settings-section-toggle" aria-expanded={settingsSection === "finance"} onClick={() => setSettingsSection(settingsSection === "finance" ? null : "finance")}>
                      <span><b>Finance</b><small>Accounts, Plaid & import rules</small></span><i aria-hidden="true">⌄</i>
                    </button>
                    {settingsSection === "finance" && <div className="settings-section-panel">
                      <p className="auth-copy">Connect and manage only your financial accounts. Nothing is shared with another household member unless you explicitly choose it.</p>
                      <FinanceSettingsPanel key={`${user?.uid}:${profile?.householdId}`} householdId={profile.householdId} ownerUid={user.uid} members={householdMembers}/>
                    </div>}
                  </section>

                  <section className={settingsSection === "calendars" ? "settings-section open" : "settings-section"}>
                    <button type="button" className="settings-section-toggle" aria-expanded={settingsSection === "calendars"} onClick={() => setSettingsSection(settingsSection === "calendars" ? null : "calendars")}>
                      <span><b>Calendar connections</b><small>Google, Apple, Outlook & iCal</small></span><i aria-hidden="true">⌄</i>
                    </button>
                    {settingsSection === "calendars" && <div className="settings-section-panel">
                      <p className="auth-copy">Bring other calendars into the family view. Connected calendars will remain clearly labeled and read-only until you choose otherwise.</p>
                      {calendarConnections.length > 0 && (
                        <section className="calendar-connections-group">
                          <div className="calendar-group-heading">
                            <h3 className="auth-subtitle">Connected calendars</h3>
                            <span>{calendarConnections.length}</span>
                          </div>
                          <div className="connected-calendar-list">
                            {calendarConnections.map((connection) => {
                              const providerName = connection.provider === "apple" ? "iCloud" : connection.provider === "google" ? "Google" : connection.provider;
                              const isBusy = connection.provider === "apple" ? appleCalendarBusy : googleCalendarBusy;
                              const lastSynced = connection.lastSyncedAt?.toDate?.();
                              return (
                                <article className="connected-calendar-row" key={connection.id}>
                                  <span className={`calendar-provider-icon ${connection.provider}`} aria-hidden="true">
                                    {connection.provider === "google" ? "G" : connection.provider === "apple" ? "●" : "↗"}
                                  </span>
                                  <div className="connected-calendar-copy">
                                    <strong>{connection.displayName}</strong>
                                    <small>{providerName} · {connection.accessMode === "read-write" ? "Read & write" : "Read only"}</small>
                                    <small>{lastSynced ? `Synced ${lastSynced.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : "Not synced yet"}</small>
                                  </div>
                                  <span className={`connection-status ${connection.status}`}>{connection.status}</span>
                                  {(connection.provider === "apple" || connection.provider === "google") && (
                                    <details className="calendar-connection-menu">
                                      <summary aria-label={`Actions for ${connection.displayName}`}>•••</summary>
                                      <div>
                                        <button type="button" disabled={isBusy} onClick={() => connection.provider === "apple" ? handleSyncAppleCalendar(connection) : handleSyncGoogleCalendar(connection)}>
                                          Sync now
                                        </button>
                                        {connection.ownerUid === user?.uid && (
                                          <button type="button" className="danger" disabled={isBusy} onClick={() => connection.provider === "apple" ? handleDisconnectAppleCalendar(connection) : handleDisconnectGoogleCalendar(connection)}>
                                            Disconnect
                                          </button>
                                        )}
                                      </div>
                                    </details>
                                  )}
                                </article>
                              );
                            })}
                          </div>
                        </section>
                      )}
                      <div className="settings-inner-divider" />
                      <div className="calendar-group-heading add-calendar-heading">
                        <h3 className="auth-subtitle">Add another calendar</h3>
                      </div>
                      <div className="calendar-provider-list">
                        {[
                          { id: "google", icon: "G", name: "Google Calendar", detail: "Sign in with Google" },
                          { id: "apple", icon: "●", name: "Apple / iCloud", detail: "Two-way sync with an app-specific password" },
                          { id: "outlook", icon: "O", name: "Outlook Calendar", detail: "Microsoft sign-in or subscription link" },
                          { id: "ical", icon: "↗", name: "Other iCal feed", detail: "Add a private .ics calendar URL" }
                        ].map((provider) => (
                          <div className="calendar-provider-row" key={provider.id}>
                            <span aria-hidden="true">{provider.icon}</span>
                            <div><strong>{provider.name}</strong><small>{provider.detail}</small></div>
                            <button type="button"
                              disabled={(appleCalendarBusy && provider.id === "apple") || (googleCalendarBusy && provider.id === "google")}
                              onClick={() => provider.id === "apple"
                                ? setAppleConnectOpen(true)
                                : provider.id === "google"
                                  ? handleStartGoogleCalendarConnection()
                                  : setCalendarConnectionStatus(`${provider.name} connection setup is ready for the secure sign-in step.`)}>
                              {provider.id === "apple" && appleCalendarBusy
                                ? "Checking…"
                                : provider.id === "google" && googleCalendarBusy
                                  ? "Opening…"
                                  : "Connect"}
                            </button>
                          </div>
                        ))}
                      </div>
                      {googleCalendars.length > 0 && (
                        <div className="auth-form">
                          <label>Google calendar
                            <select value={googleCalendarId} onChange={(event) => setGoogleCalendarId(event.target.value)}>
                              {googleCalendars.map((calendar) => (
                                <option key={calendar.id} value={calendar.id}>
                                  {calendar.name}{calendar.primary ? " (Primary)" : ""}{calendar.canCreateEvents ? "" : " — read-only"}
                                </option>
                              ))}
                            </select>
                          </label>
                          <button className="auth-submit" type="button" disabled={googleCalendarBusy || !googleCalendarId} onClick={handleConnectGoogleCalendar}>
                            {googleCalendarBusy ? "Connecting…" : "Use this calendar"}
                          </button>
                        </div>
                      )}
                      {appleConnectOpen && appleCalendars.length === 0 && (
                        <form className="auth-form" onSubmit={handleStartAppleCalendarConnection}>
                          <label>Apple Account email
                            <input name="appleUsername" type="email" autoComplete="username" required />
                          </label>
                          <label>App-specific password
                            <input name="appleAppSpecificPassword" type="password" autoComplete="new-password" required />
                          </label>
                          <p className="connection-note">This password is sent directly to the server, encrypted for your account, and never saved in this browser.</p>
                          <button className="auth-submit" type="submit" disabled={appleCalendarBusy}>
                            {appleCalendarBusy ? "Checking…" : "Continue"}
                          </button>
                        </form>
                      )}
                      {appleCalendars.length > 0 && (
                        <div className="auth-form">
                          <label>Apple family calendar
                            <select value={appleCalendarKey} onChange={(event) => setAppleCalendarKey(event.target.value)}>
                              {appleCalendars.map((calendar) => <option key={calendar.key} value={calendar.key}>{calendar.name}</option>)}
                            </select>
                          </label>
                          <button className="auth-submit" type="button" disabled={appleCalendarBusy || !appleCalendarKey} onClick={handleConnectAppleCalendar}>
                            {appleCalendarBusy ? "Connecting…" : "Use this calendar"}
                          </button>
                        </div>
                      )}
                      {calendarConnectionStatus && <p className="calendar-connection-feedback" role="status">{calendarConnectionStatus}</p>}
                      <p className="connection-note">Private calendar links and sign-in tokens will be stored only on the server—not in the browser or family-readable Firestore documents.</p>
                    </div>}
                  </section>

                  <section className={settingsSection === "notifications" ? "settings-section open" : "settings-section"}>
                    <button type="button" className="settings-section-toggle" aria-expanded={settingsSection === "notifications"} onClick={() => setSettingsSection(settingsSection === "notifications" ? null : "notifications")}>
                      <span><b>Notifications</b><small>{pushState === "on" ? "On for this device" : pushState === "blocked" ? "Blocked by browser" : pushState === "unsupported" ? "Not supported here" : "Off for this device"}</small></span><i aria-hidden="true">⌄</i>
                    </button>
                    {settingsSection === "notifications" && <div className="settings-section-panel notification-settings-panel">
                      <NotificationPreferencesPanel householdId={profile?.householdId || ""} canManageFamily={canManageFamily} uid={user.uid} pushState={pushState} pushBusy={pushBusy} pushStatus={pingStatus} onEnable={handleEnablePush} onDisable={handleDisablePush}/>
                    </div>}
                  </section>

                  <section className={settingsSection === "family" ? "settings-section open" : "settings-section"}>
                    <button type="button" className="settings-section-toggle" aria-expanded={settingsSection === "family"} onClick={() => setSettingsSection(settingsSection === "family" ? null : "family")}>
                      <span><b>Family & invitations</b><small>{visibleInvitations.filter((invite) => invite.status === "pending").length} pending</small></span><i aria-hidden="true">⌄</i>
                    </button>
                    {settingsSection === "family" && <div className="settings-section-panel">
                      <h3 className="auth-subtitle">Invite a family member</h3>
                      {!canManageFamily && <p className="family-permission-note">Only the household owner or a Parent / Admin can invite family members or change roles.</p>}
                      {canManageFamily && <form className="auth-form" onSubmit={handleInvite}>
                        <label>Email<input name="inviteEmail" type="email" placeholder="family@example.com" required /></label>
                        <label><span className="invite-role-label">Role <button type="button" className="role-info-toggle" aria-expanded={roleInfoOpen} onClick={() => setRoleInfoOpen((value) => !value)}>ⓘ What can each role do?</button></span>
                          <select name="role" defaultValue="adult">
                            <option value="admin">Parent / Admin</option>
                            <option value="adult">Adult member</option>
                            <option value="teen">Teen member</option>
                            <option value="child">Child member</option>
                          </select>
                        </label>
                        {roleInfoOpen && <div className="role-info-panel">
                          <div><strong>Parent / Admin</strong><span>Can manage the household, invitations, shared layouts, widgets, and display profiles. Can connect their own financial accounts.</span></div>
                          <div><strong>Adult member</strong><span>Can use the family dashboard, personalize their experience, and connect their own financial accounts. Cannot manage the household or other members.</span></div>
                          <div><strong>Teen member</strong><span>Same everyday access as an adult, including connecting their own financial accounts, but cannot manage the household or other members.</span></div>
                          <div><strong>Child member</strong><span>Can use and personalize the family dashboard. Cannot manage the household or connect financial accounts.</span></div>
                          <small>Financial accounts stay private to the person who connects them unless they explicitly share access.</small>
                        </div>}
                        {authError && <p className="auth-error" role="alert">{authError}</p>}
                        {inviteStatus && <p className="auth-success" role="status">{inviteStatus}</p>}
                        <button className="auth-submit" type="submit" disabled={authBusy}>{authBusy ? "Preparing…" : "Prepare invitation"}</button>
                      </form>}
                      <div className="settings-inner-divider" />
                      <div className="invite-heading">
                        <h3 className="auth-subtitle">Family members & invitations</h3>
                        <span>{visibleInvitations.filter((invite) => invite.status === "pending").length}</span>
                      </div>
                      <div className="invite-list">
                        {visibleInvitations.length === 0 ? <p className="invite-empty">No invitations yet.</p> : visibleInvitations.map((invite) => (
                          <div className="invite-row" key={invite.id}>
                            <div><strong>{invite.email}</strong><small>{familyRoleLabel(invite.role)}</small></div>
                            <span className={`invite-status ${invite.status}`}>{invite.status === "accepted" ? "accepted" : invite.status === "cancelled" ? "cancelled" : invite.deliveryStatus === "sent" ? "emailed" : invite.status}</span>
                            {invite.status === "accepted" ? (
                              <select
                                className="invite-role-select"
                                aria-label={`Role for ${invite.email}`}
                                value={invite.role}
                                disabled={!canManageFamily || authBusy || !invite.acceptedBy}
                                onChange={(event) => handleChangeMemberRole(invite, event.target.value)}
                              >
                                <option value="admin">Parent / Admin</option>
                                <option value="adult">Adult member</option>
                                <option value="teen">Teen member</option>
                                <option value="child">Child member</option>
                              </select>
                            ) : canManageFamily ? (
                              <div className="invite-actions"><button type="button" disabled={authBusy || invite.status !== "pending"} onClick={() => handleResendInvite(invite)}>Resend</button><button type="button" className="invite-cancel" disabled={authBusy || invite.status !== "pending"} onClick={() => handleCancelInvite(invite)}>Cancel</button></div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>}
                  </section>

                  <section className={settingsSection === "account" ? "settings-section open" : "settings-section"}>
                    <button type="button" className="settings-section-toggle" aria-expanded={settingsSection === "account"} onClick={() => setSettingsSection(settingsSection === "account" ? null : "account")}>
                      <span><b>Account</b><small>{user.email}</small></span><i aria-hidden="true">⌄</i>
                    </button>
                    {settingsSection === "account" && <div className="settings-section-panel account-panel">
                      <button type="button" className="account-avatar-card" onClick={() => setAvatarOpen(true)}>
                        <AvatarPortrait avatar={currentMember?.avatar} />
                        <span><strong>Make my avatar</strong><small>Hair, clothes, face, colors, and more</small></span>
                      </button>
                      <p className="auth-copy">Signed in as <strong>{user.email}</strong>. Each family member controls only their own avatar.</p>
                      <AccountSecurityPanel user={user} />
                      <button type="button" className="auth-link auth-signout" onClick={async () => { try { await detachPushBeforeSignOut(); await signOut(auth); setAuthOpen(false); } catch { setAuthError("Could not detach this device’s alerts. Please retry sign out."); } }}>Sign out</button>
                    </div>}
                  </section>
                </div>
              </>
            )}          </section>
        </div>
      )}

      {avatarOpen && user && profile?.householdId && (
        <AvatarEditor
          householdId={profile.householdId}
          userUid={user.uid}
          initialAvatar={currentMember?.avatar}
          onClose={() => setAvatarOpen(false)}
          onSaved={() => setAvatarOpen(false)}
        />
      )}

    </div>
  );
}

function MarketingLanding({ onSignIn, onCreateAccount }: { onSignIn: () => void; onCreateAccount: () => void }) {
  const comparisons = [
    { feature: "Shared family calendar", free: true, plus: true },
    { feature: "Lists, tasks, and countdowns", free: true, plus: true },
    { feature: "Family Face-Off, streaks, and Home Hunt", free: true, plus: true },
    { feature: "Weather, tides, sun, and moon", free: true, plus: true },
    { feature: "Family Check-Ins and notifications", free: true, plus: true },
    { feature: "Bank and investment connections", free: false, plus: true },
    { feature: "Expenses, income, and cash flow", free: false, plus: true },
    { feature: "Investments, net worth, and goals", free: false, plus: true }
  ];

  return (
    <div className="public-landing">
      <PublicSupportChat />
      <header className="landing-nav">
        <a className="landing-brand" href="#top" aria-label="FamDashFun home">
          <span aria-hidden="true">⌂</span>
          <strong>FamDashFun</strong>
        </a>
        <nav aria-label="Public site">
          <a href="#features">What it does</a>
          <a href="#plans">Plans</a>
          <button type="button" onClick={onSignIn}>Log in</button>
        </nav>
      </header>

      <main id="top">
        <section className="landing-hero">
          <div className="landing-hero-copy">
            <p className="eyebrow">Your family’s favorite tab</p>
            <h1>One happy place for the whole household.</h1>
            <p>Plans, lists, weather, games, memories, reminders, and the occasional family bragging right—all together and actually fun to use.</p>
            <div className="landing-hero-actions">
              <button type="button" className="landing-primary" onClick={onCreateAccount}>Start your family dashboard</button>
              <button type="button" className="landing-secondary" onClick={onSignIn}>I already have one</button>
            </div>
            <small>No spreadsheets were harmed in the making of this family.</small>
          </div>

          <div className="landing-dashboard-peek" aria-label="A playful preview of the FamDashFun dashboard">
            <div className="landing-peek-top">
              <span><i aria-hidden="true">●</i> The Fun Family</span>
              <b>Tuesday</b>
            </div>
            <article className="landing-peek-pulse">
              <small>Family pulse</small>
              <strong>Everybody has plans. Miraculously, some of them match.</strong>
            </article>
            <div className="landing-peek-grid">
              <article className="landing-peek-calendar"><span aria-hidden="true">▣</span><div><small>Up next</small><strong>Taco night</strong><b>6:00 PM</b></div></article>
              <article className="landing-peek-weather"><span aria-hidden="true">☀</span><div><small>Outside</small><strong>72°</strong><b>Beach approved</b></div></article>
              <article className="landing-peek-game"><small>Family Face-Off</small><strong>Who is most likely to eat the leftovers?</strong><div><span>Ben</span><span>Sheri</span><span>The kids</span></div></article>
            </div>
            <span className="landing-spark landing-spark-one" aria-hidden="true">★</span>
            <span className="landing-spark landing-spark-two" aria-hidden="true">✦</span>
          </div>
        </section>

        <section className="landing-section" id="features">
          <div className="landing-section-heading">
            <p className="eyebrow">Less “who knew?” More “it’s on the dash.”</p>
            <h2>Family life, minus twelve different apps.</h2>
            <p>FamDashFun keeps the useful stuff organized and leaves room for the stuff that makes your family yours.</p>
          </div>
          <div className="landing-feature-grid">
            {[
              { icon: "▣", title: "Everyone knows the plan", copy: "Calendars, countdowns, reminders, and family notifications stay in one shared rhythm." },
              { icon: "☷", title: "Lists that get finished-ish", copy: "Shopping and around-the-house tasks are easy to add, assign, and cross off." },
              { icon: "★", title: "A little friendly chaos", copy: "Face-Off questions, streaks, Home Hunt, scores, and Family Book keep everyone playing." },
              { icon: "☀", title: "Ready for outside", copy: "Weather, tides, sunrise, sunset, and moon details help the family pick the right adventure." }
            ].map((feature) => (
              <article key={feature.title}>
                <span aria-hidden="true">{feature.icon}</span>
                <h3>{feature.title}</h3>
                <p>{feature.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-section landing-plans" id="plans">
          <div className="landing-section-heading">
            <p className="eyebrow">Pick your family flavor</p>
            <h2>Start free. Add the money stuff when you’re ready.</h2>
            <p>The free dashboard skips all linked-finance tabs. Family Plus is the planned home for secure bank and investment connections.</p>
          </div>

          <div className="landing-price-grid">
            <article className="landing-price-card">
              <p className="landing-plan-name">Free</p>
              <h3>$0 <small>forever sounds nice</small></h3>
              <p>Everything a family needs to stay coordinated, connected, and mildly competitive.</p>
              <ul>
                <li>Shared family dashboard</li>
                <li>Calendar, lists, and countdowns</li>
                <li>Games, scores, and Family Book</li>
                <li>Weather, tides, and family alerts</li>
              </ul>
              <button type="button" className="landing-secondary" onClick={onCreateAccount}>Get started free</button>
            </article>

            <article className="landing-price-card featured">
              <span className="landing-coming-soon">Planned</span>
              <p className="landing-plan-name">Family Plus</p>
              <h3>$9.99 <small>per month</small></h3>
              <p>The whole free dashboard, plus a private financial command center for the adults you choose.</p>
              <ul>
                <li>Secure bank and investment connections</li>
                <li>Expenses, income, and cash flow</li>
                <li>Holdings, net worth, and goal planning</li>
                <li>Owner-controlled financial sharing</li>
              </ul>
              <button type="button" className="landing-primary" onClick={onCreateAccount}>Start free for now</button>
            </article>
          </div>

          <div className="landing-comparison">
            <div className="landing-comparison-head">
              <span>Compare plans</span><b>Free</b><b>Family Plus</b>
            </div>
            {comparisons.map((row) => (
              <div className="landing-comparison-row" key={row.feature}>
                <span>{row.feature}</span>
                <b aria-label={row.free ? "Included in Free" : "Not included in Free"}>{row.free ? "✓" : "—"}</b>
                <b aria-label={row.plus ? "Included in Family Plus" : "Not included in Family Plus"}>{row.plus ? "✓" : "—"}</b>
              </div>
            ))}
          </div>
          <p className="landing-plan-note">Family Plus pricing and features are an early plan, not a current purchase offer. Financial connections will launch only after security testing is complete.</p>
        </section>

        <section className="landing-cta">
          <span aria-hidden="true">⌂</span>
          <div>
            <p className="eyebrow">Bring everybody home</p>
            <h2>Your family dashboard is waiting.</h2>
            <p>Start with the fun, useful stuff. The rest can grow with your family.</p>
          </div>
          <button type="button" onClick={onCreateAccount}>Create your dashboard</button>
        </section>
      </main>

      <footer className="landing-footer">
        <a className="landing-brand" href="#top"><span aria-hidden="true">⌂</span><strong>FamDashFun</strong></a>
        <p>Made for families who have a lot going on.</p>
        <button type="button" onClick={onSignIn}>Dashboard login</button>
      </footer>
    </div>
  );
}

function CardHeading({ label, title, action, plus = false, onAdd, onOpen }: { label: string; title: string; action: string; plus?: boolean; onAdd?: () => void; onOpen?: () => void }) {
  return (
    <div className="home-card-heading">
      {onOpen ? <button type="button" className="home-card-heading-link" onClick={onOpen}><p className="leader-label">{label}</p><h3>{title}</h3></button> : <div><p className="leader-label">{label}</p><h3>{title}</h3></div>}
      <div className="heading-action"><span>{action}</span>{plus && <button type="button" aria-label={`Add to ${title}`} onClick={onAdd}>+</button>}</div>
    </div>
  );
}

function WeatherHourlyChart({ weather, view, now }: { weather: WeatherData; view: WeatherView; now: Date }) {
  const points = weather.hourly.slice(0, 24);
  if (!points.length) return <div className="weather-hourly-chart unavailable">Hourly forecast unavailable.</div>;
  const currentIndex = Math.min(Math.max(0, now.getHours()), Math.max(0, points.length - 1));
  const primary = points.map((point) => view === "overview" ? point.temperature : view === "uv" ? point.uvIndex : view === "wind" ? point.windSpeed : point.precipitationChance);
  const secondary = view === "wind" ? points.map((point) => point.windGust) : [];
  const values = [...primary, ...secondary];
  const rawMin = view === "overview" ? Math.min(...values) : 0;
  const rawMax = Math.max(...values, view === "precipitation" ? 100 : 1);
  const padding = view === "overview" ? Math.max(2, (rawMax - rawMin) * .18) : 0;
  const min = rawMin - padding;
  const max = view === "precipitation" ? 100 : rawMax + (view === "overview" ? padding : rawMax * .08);
  const x = (index: number) => 30 + (index / Math.max(1, points.length - 1)) * 440;
  const y = (value: number) => 118 - ((value - min) / Math.max(1, max - min)) * 82;
  const line = (series: number[]) => series.map((value, index) => `${index ? "L" : "M"}${x(index).toFixed(1)} ${y(value).toFixed(1)}`).join(" ");
  const currentValue = primary[currentIndex] ?? 0;
  const metric = view === "overview" ? `${Math.round(currentValue)}°`
    : view === "uv" ? currentValue.toFixed(1)
    : view === "wind" ? `${Math.round(currentValue)} mph`
    : `${Math.round(currentValue)}%`;
  const detail = view === "overview" ? `${weatherLabel(weather.weatherCode)} · feels ${weather.apparentTemperature}°`
    : view === "uv" ? `${uvLabel(currentValue)} UV now`
    : view === "wind" ? `${compassDirection(weather.windDirection)} · gusts ${Math.round(points[currentIndex]?.windGust || 0)} mph`
    : `${(points[currentIndex]?.precipitationAmount || 0).toFixed(2)} in this hour`;
  const yTick = (value: number) => view === "overview" ? `${Math.round(value)}°`
    : view === "uv" ? value.toFixed(1)
    : view === "wind" ? `${Math.round(value)} mph`
    : `${Math.round(value)}%`;

  return (
    <div className={`weather-hourly-chart ${view}`} aria-label={`Hourly ${view} forecast for today`}>
      <div className="weather-chart-metric"><strong>{metric}</strong><span>{detail}</span></div>
      <svg viewBox="0 0 500 150" role="img">
        <path className="weather-grid" d="M30 36H470 M30 77H470 M30 118H470 M30 36V118 M140 36V118 M250 36V118 M360 36V118 M470 36V118" />
        {view === "precipitation" && points.map((point, index) => {
          const barHeight = Math.min(32, (point.precipitationAmount / Math.max(.01, ...points.map((hour) => hour.precipitationAmount))) * 32);
          return <rect className="precipitation-bar" key={point.time} x={x(index) - 5} y={118 - barHeight} width="10" height={barHeight} rx="2" />;
        })}
        <path className="weather-area" d={`${line(primary)} L470 118 L30 118 Z`} />
        {secondary.length > 0 && <path className="weather-secondary-line" d={line(secondary)} />}
        <path className="weather-primary-line" d={line(primary)} />
        <line className="weather-now-line" x1={x(currentIndex)} y1="32" x2={x(currentIndex)} y2="118" />
        <circle className="weather-now-dot" cx={x(currentIndex)} cy={y(currentValue)} r="6" />
        <g className="weather-y-axis">
          <text x="466" y="45">{yTick(max)}</text>
          <text x="466" y="81">{yTick((max + min) / 2)}</text>
          <text x="466" y="116">{yTick(min)}</text>
        </g>
        <g className="weather-axis"><text x="30" y="141">12am</text><text x="140" y="141">6am</text><text x="250" y="141">12pm</text><text x="360" y="141">6pm</text><text x="470" y="141">12am</text></g>
      </svg>
    </div>
  );
}

function MiniEvent({ label, time, value }: { label: string; time: string; value: string }) {
  return <div><span>{label}</span><strong>{time}</strong><b>{value}</b></div>;
}

function Countdown({ name, value, unit, onClick }: { name: string; value: string; unit: string; onClick: () => void }) {
  return (
    <button type="button" className="countdown-item" onClick={onClick} aria-label={`Edit ${name} countdown`}>
      <span aria-hidden="true">★</span>
      <strong>{name}</strong>
      <b>{value}{unit && <small> {unit}</small>}</b>
    </button>
  );
}
